import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Loader2, ArrowLeft } from "lucide-react";
import { createApplication, updateApplication } from "../../api/borrowerPortal/applications.js";
import { getBorrowerEmail } from "../../utils/auth";
import Card from "../../components/ui/Card.jsx";
import PhoneField from "../../components/ui/PhoneField.jsx";
import { applicationFormPhones, arePhonesValid } from "../../utils/validatePhone.js";
import Stepper from "./steps/Stepper.jsx";
import Wizard from "./Wizard.jsx";
import SleekUploadTile from "../../components/ui/SleekUploadTile.jsx";
import { attachFileToApplication, uploadFileToApplication } from "../../api/borrowerPortal/uploadFile.js";
import { shortlistPathForCategory } from "../../utils/sourcingCategory.js";
import { formatProductCategoryLabel } from "../../utils/complexBtlProduct.js";
import {
  IF_UPLOAD_BUCKETS,
  IF_UPLOAD_FIELD_ALIASES,
  normalizeIfUploads,
} from "./forms/shared/borrowerUploadConfig.js";

const INTENT_KEY = "ds_app_intent";
const INTENT_TTL_MS = 15 * 60 * 1000; // 15 minutes
const SUPPORTED_TYPES = ["BTL", "CBTL", "IF", "ASSET"]; // extend when others ready

// Map UI ids → internal borrower keys expected by Wizard/validation
const BORROWER_KEY = {
  solo: "individual",      // Sole trader
  partnership: "partnership",
  limited: "limited",      // Ltd Co & LLP
  pension: "pension",
};

export default function NewApplication({
  selectedBorrowerType,        // optional external control
  setSelectedBorrowerType,     // optional external control
  onSubmit,                    // optional final submit handler
}) {
  const { state } = useLocation();
  const navigate = useNavigate();

  // ---- Resolve application intent (productId + productType) ----
  const intent = useMemo(() => {
    if (state?.productId && state?.productType) return state; // prefer navigation state
    try {
      const raw = sessionStorage.getItem(INTENT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const fresh = parsed?.ts && Date.now() - parsed.ts < INTENT_TTL_MS;
      if (fresh && parsed.productId && parsed.productType) return parsed;
    } catch { }
    return null;
  }, [state]);

  // Guard: no valid intent
  useEffect(() => {
    if (!intent) {
      toast("Start from your shortlisted products to create an application.");
      navigate("/borrower/applications?reason=no-intent", { replace: true });
    }
  }, [intent, navigate]);

  // Normalize product meta
  const productType = useMemo(
    () => (intent?.productType ? String(intent.productType).toUpperCase() : null),
    [intent]
  );
  const productId = useMemo(
    () => (intent?.productId ? String(intent.productId) : null),
    [intent]
  );

  // Extract loan purpose from product if available, otherwise default to "Purchase"
  const loanPurpose = useMemo(() => {
    // 1. Check if intent has an explicit loanPurpose at top level
    if (intent?.loanPurpose && typeof intent.loanPurpose === 'string' && intent.loanPurpose.trim() !== '') {
      return intent.loanPurpose.trim();
    }

    // 2. Check within the product object
    const product = intent?.product;
    if (product) {
      // Check various possible field names for loan purpose
      // Also check _raw field (original product data from backend)
      const rawProduct = product._raw || {};
      const purpose = product.loan_purpose ||
        product.loanPurpose ||
        product.loan_purpose_type ||
        product.purpose ||
        rawProduct.loan_purpose ||
        rawProduct.loanPurpose ||
        rawProduct.loan_purpose_type ||
        rawProduct.purpose;
      if (purpose && typeof purpose === 'string' && purpose.trim() !== '') {
        return purpose.trim();
      }
    }
    // Default to "Purchase" if not found
    if (String(intent?.productType || "").toUpperCase() === "IF") return "Invoice Finance";
    return "Purchase";
  }, [intent]);

  // Guard unsupported types
  useEffect(() => {
    if (productType && !SUPPORTED_TYPES.includes(productType)) {
      console.error("Unsupported product type:", productType, "Supported types:", SUPPORTED_TYPES, "Intent:", intent);
      toast.error(`Unsupported product type: ${productType}. Please choose another product.`);
      navigate("/borrower/applications?reason=bad-product", { replace: true });
    }
  }, [productType, navigate, intent]);

  // ---- Stepper: 0 Borrower type → 1 Application Form → 2 Review & Submit
  const [step, setStep] = useState(0);

  // ---- Borrower type local/controlled state ----
  const [internalType, setInternalType] = useState(null);
  const uiType = selectedBorrowerType ?? internalType;  // "solo" | "partnership" | "limited" | "pension"
  const setUiType = setSelectedBorrowerType ?? setInternalType;

  // Translate UI id → internal key for Wizard
  const borrowerType = uiType ? BORROWER_KEY[uiType] : null;

  // Data from the Application Form (Wizard onSubmit/onNext result)
  const [draftApp, setDraftApp] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingApplicationName, setExistingApplicationName] = useState(null); // Store applicationName from Wizard
  const submitInFlightRef = useRef(false);

  // Show loading state while intent is being resolved
  if (!intent || !productType || !productId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-600" />
          <p className="mt-4 text-slate-600">Loading application form...</p>
        </div>
      </div>
    );
  }

  // ✅ Helper function to extract and attach temporary files to application
  const attachTemporaryFiles = async (applicationName, draftApp) => {
    try {
      console.log("🔍 Starting file attachment process for application:", applicationName);
      console.log("🔍 DraftApp structure:", JSON.stringify(draftApp, null, 2));

      // ✅ Map frontend field names to backend field names
      const btlFieldNameMapping = {
        // Borrower documents
        'borrower_accounts': 'borrower_property_accounts',
        'borrower_bank_statements': 'borrower_bank_statements',
        'borrower_portfolio': 'borrower_property_portfolio',
        // Applicant documents
        'app_assets_liability': 'applicant_assets_liability',
        'app_self_employed': 'applicant_self_employed_docs',
        'app_employed': 'applicant_employed_docs',
        'app_personal_bank': 'applicant_personal_bank_statements',
        'app_id': 'applicant_identification',
      };

      // Helper to map field name to backend field name
      const mapFieldName = (frontendFieldName, { isIf = false } = {}) => {
        if (isIf) {
          if (frontendFieldName === "app_id") return "if_id_passport";
          if (IF_UPLOAD_FIELD_ALIASES[frontendFieldName]) {
            return IF_UPLOAD_FIELD_ALIASES[frontendFieldName];
          }
        }
        return btlFieldNameMapping[frontendFieldName] || frontendFieldName;
      };

      // Extract uploads from specific known locations to avoid duplicates
      const seenFiles = new Set(); // Track seen files to avoid duplicates
      const tempFiles = [];

      // Helper to add file if not seen
      const addFileIfUnique = (file_url, frontendFieldname, opts = {}) => {
        if (!file_url || !frontendFieldname) {
          console.warn("⚠️ Skipping file with missing file_url or fieldname:", { file_url, frontendFieldname });
          return;
        }
        // Map to backend field name
        const backendFieldname = mapFieldName(frontendFieldname, opts);
        const key = `${file_url}|${backendFieldname}`;
        if (!seenFiles.has(key)) {
          seenFiles.add(key);
          tempFiles.push({ file_url, fieldname: backendFieldname, originalFieldname: frontendFieldname });
          console.log(`✅ Added file to attach list: ${file_url} -> ${frontendFieldname} (mapped to ${backendFieldname})`);
        } else {
          console.log(`⏭️ Skipping duplicate file: ${file_url} -> ${backendFieldname}`);
        }
      };

      // Extract from known upload locations
      const extractFromUploads = (uploadsObj, location = "unknown", opts = {}) => {
        if (!uploadsObj || typeof uploadsObj !== 'object') {
          console.log(`📦 No uploads found at location: ${location}`);
          return;
        }

        console.log(`📦 Extracting uploads from location: ${location}`, uploadsObj);

        Object.entries(uploadsObj).forEach(([bucketKey, files]) => {
          if (Array.isArray(files)) {
            console.log(`📁 Found ${files.length} file(s) in bucket: ${bucketKey}`);
            files.forEach((file, index) => {
              console.log(`  File ${index + 1}:`, file);
              if (file?._temp && file?.file_url && file?._fieldname) {
                addFileIfUnique(file.file_url, file._fieldname, opts);
              } else if (file?.file_url && opts.isIf && (file._fieldname || bucketKey)) {
                // IF: allow attaching even if _temp/_fieldname partially missing (legacy drafts)
                addFileIfUnique(file.file_url, file._fieldname || bucketKey, opts);
              } else {
                console.warn(`  ⚠️ File ${index + 1} missing required fields:`, {
                  has_temp: !!file?._temp,
                  has_file_url: !!file?.file_url,
                  has_fieldname: !!file?._fieldname,
                  file
                });
              }
            });
          } else {
            console.log(`⚠️ Bucket ${bucketKey} is not an array:`, typeof files);
          }
        });
      };

      // Check multiple possible locations for uploads
      const fullPayload = draftApp?.fullPayload || draftApp?.payloadObj || draftApp || {};

      console.log("🔍 Full payload structure:", {
        hasBtl: !!fullPayload.btl,
        hasIf: !!fullPayload.if,
        hasFormData: !!fullPayload.formData,
        hasUploads: !!fullPayload.uploads,
        btlKeys: fullPayload.btl ? Object.keys(fullPayload.btl) : [],
        ifKeys: fullPayload.if ? Object.keys(fullPayload.if) : [],
      });

      // Check btl structure uploads
      if (fullPayload.btl) {
        if (fullPayload.btl.solo?.uploads) {
          extractFromUploads(fullPayload.btl.solo.uploads, "btl.solo.uploads");
        }
        if (fullPayload.btl.limited?.uploads) {
          extractFromUploads(fullPayload.btl.limited.uploads, "btl.limited.uploads");
        }
        if (fullPayload.btl.partnership?.uploads) {
          extractFromUploads(fullPayload.btl.partnership.uploads, "btl.partnership.uploads");
        }
        if (fullPayload.btl.pension?.uploads) {
          extractFromUploads(fullPayload.btl.pension.uploads, "btl.pension.uploads");
        }
        if (fullPayload.btl.solo_uploads) {
          extractFromUploads(fullPayload.btl.solo_uploads, "btl.solo_uploads");
        }
      }

      if (fullPayload.if) {
        if (fullPayload.if.solo?.uploads) extractFromUploads(fullPayload.if.solo.uploads, "if.solo.uploads", { isIf: true });
        if (fullPayload.if.limited?.uploads) extractFromUploads(fullPayload.if.limited.uploads, "if.limited.uploads", { isIf: true });
        if (fullPayload.if.partnership?.uploads) extractFromUploads(fullPayload.if.partnership.uploads, "if.partnership.uploads", { isIf: true });
        if (fullPayload.if.pension?.uploads) extractFromUploads(fullPayload.if.pension.uploads, "if.pension.uploads", { isIf: true });
      }

      // Check formData uploads
      if (fullPayload.formData?.uploads) {
        const isIfPayload = !!fullPayload.if || String(productType || "").toUpperCase() === "IF";
        extractFromUploads(fullPayload.formData.uploads, "formData.uploads", { isIf: isIfPayload });
      }

      // Check top-level uploads
      if (fullPayload.uploads) {
        const isIfPayload = !!fullPayload.if || String(productType || "").toUpperCase() === "IF";
        extractFromUploads(fullPayload.uploads, "uploads", { isIf: isIfPayload });
      }

      console.log(`📎 Found ${tempFiles.length} unique temporary file(s) to attach:`, tempFiles);

      if (tempFiles.length === 0) {
        console.warn("⚠️ No temporary files found to attach. Checking if files exist in payload...");
        // Debug: log the entire payload structure to see where files might be
        console.log("🔍 Full payload for debugging:", JSON.stringify(fullPayload, null, 2));
        return;
      }

      // Attach each temporary file to the application
      let successCount = 0;
      let failCount = 0;

      for (const file of tempFiles) {
        try {
          console.log(`📎 Attaching file ${file.file_url} to field ${file.fieldname} (original: ${file.originalFieldname || 'N/A'})...`);
          const result = await attachFileToApplication({
            fileUrl: file.file_url,
            applicationName,
            fieldname: file.fieldname, // Use mapped backend field name
          });
          console.log(`✅ Successfully attached ${file.file_url} to ${file.fieldname}:`, result);
          successCount++;
        } catch (error) {
          console.error(`❌ Failed to attach file ${file.file_url} to ${file.fieldname}:`, error);
          console.error("Error details:", {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
          });
          failCount++;
          // Continue with other files even if one fails
        }
      }

      console.log(`✅ Completed attaching files: ${successCount} succeeded, ${failCount} failed out of ${tempFiles.length} total`);
    } catch (error) {
      console.error("❌ Error in attachTemporaryFiles:", error);
      console.error("Error stack:", error?.stack);
      // Don't block submission if file attachment fails
    }
  };

  const handleFinalSubmit = async (extraPayload, dataOverride = null) => {
    if (submitInFlightRef.current || isSubmitting) {
      return;
    }

    // CRITICAL: Only allow submission if declarations are explicitly provided
    const extraData = (extraPayload && !extraPayload.nativeEvent) ? extraPayload : {};
    const appData = dataOverride || draftApp;

    // ✅ STRENGTHEN GUARDS: Require explicit true values
    if (extraData.agree1 !== true || extraData.agree2 !== true) {
      console.warn("⚠️ Submission blocked: Declarations not accepted", {
        agree1: extraData.agree1,
        agree2: extraData.agree2
      });
      toast.error("Please accept both declarations to submit the application.");
      return;
    }

    // Additional guard: Ensure we have valid draftApp data
    if (!appData) {
      console.warn("⚠️ Submission blocked: No application data found");
      toast.error("No application data found. Please complete the form first.");
      return;
    }

    // Additional guard: Ensure we're not on step 0
    if (step === 0) {
      console.warn("⚠️ Submission blocked: User is on borrower type selection step");
      return;
    }

    console.log("✅ Explicit submission initiated by user - declarations accepted");
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(appData); // Allow external override
      } else {
        // Get borrower email
        const applicantEmail = getBorrowerEmail();
        if (!applicantEmail) {
          throw new Error("User not authenticated");
        }

        // Get product info from intent
        // Intent is already resolved in component scope (see line 32)

        const product = intent?.product || {};
        const productId = intent?.productId || appData.productId;
        const productType = intent?.productType || appData.productType || "BTL";
        const docProductType = String(productType).toUpperCase() === "CBTL" ? "BTL" : productType;
        const loanPurpose = intent?.loanPurpose || appData.loanPurpose || "Purchase";

        if (!productId) {
          toast.error("Missing product information. Please restart.");
          setIsSubmitting(false);
          return;
        }

        // Backend Applications doctype requires shortlisted_product (Shortlisted Product doc name)
        const shortlistedProductDocName = product._isDummy
          ? null
          : (product.shortlisted_doc_name ?? product.shortlisted_product ?? product.name);
        if (!shortlistedProductDocName && !applicationName && !product._isDummy) {
          toast.error("Missing shortlisted product reference. Please apply from your shortlisted products.");
          setIsSubmitting(false);
          return;
        }

        // Merge declarations into draftApp
        let dataForMapping = { ...appData };
        if (!dataForMapping.formData) dataForMapping.formData = {};
        dataForMapping.formData.agree1 = extraData.agree1;
        dataForMapping.formData.agree2 = extraData.agree2;

        if (dataForMapping.btl) {
          if (dataForMapping.btl.solo) {
            dataForMapping.btl.solo_agree1 = extraData.agree1;
            dataForMapping.btl.solo_agree2 = extraData.agree2;
          }
          if (dataForMapping.btl.limited) {
            dataForMapping.btl.limited.agree1 = extraData.agree1;
            dataForMapping.btl.limited.agree2 = extraData.agree2;
          }
          if (dataForMapping.btl.partnership) {
            dataForMapping.btl.partnership.agree1 = extraData.agree1;
            dataForMapping.btl.partnership.agree2 = extraData.agree2;
          }
          if (dataForMapping.btl.pension) {
            dataForMapping.btl.pension.agree1 = extraData.agree1;
            dataForMapping.btl.pension.agree2 = extraData.agree2;
          }
        }

        if (dataForMapping.if) {
          if (dataForMapping.if.solo) {
            dataForMapping.if.solo.agree1 = extraData.agree1;
            dataForMapping.if.solo.agree2 = extraData.agree2;
          }
          if (dataForMapping.if.limited) {
            dataForMapping.if.limited.agree1 = extraData.agree1;
            dataForMapping.if.limited.agree2 = extraData.agree2;
          }
          if (dataForMapping.if.partnership) {
            dataForMapping.if.partnership.agree1 = extraData.agree1;
            dataForMapping.if.partnership.agree2 = extraData.agree2;
          }
          if (dataForMapping.if.pension) {
            dataForMapping.if.pension.agree1 = extraData.agree1;
            dataForMapping.if.pension.agree2 = extraData.agree2;
          }
        }

        // Ensure borrowerType is extracted correctly
        const actualBorrowerType = borrowerType || appData?.borrowerType || null;
        console.log("=== BORROWER TYPE CHECK ===");
        console.log("borrowerType from state:", borrowerType);
        console.log("draftApp.borrowerType:", appData?.borrowerType);
        console.log("Using actualBorrowerType:", actualBorrowerType);
        console.log("==========================");

        const applicationData = mapFormDataToApplication(dataForMapping, actualBorrowerType, productType);

        // Debug: Log the mapped data
        console.log("=== MAPPING DEBUG ===");
        console.log("Original draftApp:", JSON.stringify(appData, null, 2));
        console.log("borrowerType used:", actualBorrowerType);
        console.log("productType:", productType);
        console.log("Mapped application data:", JSON.stringify(applicationData, null, 2));
        console.log("===================");

        // ✅ CRITICAL: Check if application already exists
        const applicationName = existingApplicationName || appData?.applicationName || null;

        // Extract metadata from intent if available
        const meta = intent?.meta || (intent?.sourcingInputs ? {
          sourcingInputs: intent.sourcingInputs,
          filters: intent.filters || {},
          shortlistedProducts: intent.shortlistedProducts || []
        } : (appData?.meta || {}));

        // Get repayment method from sourcing inputs or payload
        const repaymentMethod = meta?.sourcingInputs?.repaymentType ||
          meta?.filters?.repaymentType ||
          appData?.repaymentType ||
          appData?.repayment_type ||
          "Repayment";

        if (applicationName) {
          // Application exists - UPDATE it with final data
          console.log("=== UPDATING EXISTING APPLICATION (EXPLICIT SUBMISSION) ===");

          const fullPayloadForBackend = appData?.fullPayload || appData?.payloadObj || appData || {};

          if (applicationData._allApplicants) {
            if (!fullPayloadForBackend.formData) fullPayloadForBackend.formData = {};
            if (!fullPayloadForBackend.formData.applicants) {
              fullPayloadForBackend.formData.applicants = applicationData._allApplicants;
            }
            if (!fullPayloadForBackend.btl) fullPayloadForBackend.btl = {};
            if (actualBorrowerType === "limited" && !fullPayloadForBackend.btl.limited) {
              fullPayloadForBackend.btl.limited = { applicants: applicationData._allApplicants };
            } else if (actualBorrowerType === "partnership" && !fullPayloadForBackend.btl.partnership) {
              fullPayloadForBackend.btl.partnership = { partners: applicationData._allApplicants };
            } else if (actualBorrowerType === "pension" && !fullPayloadForBackend.btl.pension) {
              fullPayloadForBackend.btl.pension = { members: applicationData._allApplicants };
            }
          }

          const loanAmountFromProduct = product.loan_amount ?? product.mortgageAmount ?? product.mortgage_amount ?? product.loan_amount;
          const propertyValueFromProduct = product.property_value ?? product.propertyValue ?? product._raw?.property_value ?? product._raw?.propertyValue;
          const repaymentTypeFromProduct = product.repayment_type ?? product.repaymentType ?? product.repayment_method ?? repaymentMethod;
          const costOverProductTermFromProduct = product.cost_over_product_term ?? product.total_cost ?? product.totalCost ?? fullPayloadForBackend.cost_over_product_term;
          const monthlyPaymentFromProduct = product.monthly_payment ?? product.monthlyPayment ?? product.monthlyCost ?? product.monthly_repayments ?? product.repayment ?? product._raw?.monthly_payment ?? product._raw?.monthly_repayments ?? fullPayloadForBackend.monthly_payment;

          const ifFieldsFromProduct = String(productType || "").toUpperCase() === "IF" ? {
            sector: product.sector ?? product._raw?.sector ?? null,
            projected_turnover: product.projected_turnover ?? product.projectedTurnover ?? 0,
            facility_required: product.facility_required ?? product.facilityRequired ?? product.headlineAmount ?? loanAmountFromProduct ?? 0,
            facility_type: (() => {
              const existing = String(product.facility_type ?? product.facilityType ?? "").trim();
              if (/^confidential$/i.test(existing)) return "Confidential";
              if (/^disclosure$|^disclosed$/i.test(existing)) return "Disclosure";
              const conf =
                product.confidential_facility ??
                product.confidentialFacility ??
                product.confidential ??
                0;
              return conf === true || conf === 1 || conf === "1" ? "Confidential" : "Disclosure";
            })(),
            net_advance: product.net_advance ?? product.netAdvance ?? 0,
            additional_borrowing_amount: product.additional_borrowing_amount ?? product.additionalBorrowingAmount ?? product.additionalLoanAmount ?? 0,
            additional_borrowing: product.additional_borrowing ?? ((product.additional_borrowing_amount || product.additionalLoanAmount) ? 1 : 0),
            confidential_facility: product.confidential_facility ?? product.confidentialFacility ?? 0,
            confidential: product.confidential ?? 0,
            disclosure: product.disclosure ?? 0,
            ip_percent: product.ip_percent ?? product.invoicePurchasePct ?? 0,
            discount_rate_percent: product.discount_rate_percent ?? product.discountRatePercent ?? 0,
            simple_service_fee: product.simple_service_fee ?? product.annualCostPct ?? 0,
            min_service_fee: product.min_service_fee ?? product.minServiceFee ?? 0,
            apr: product.apr ?? product.ratePct ?? product.rate ?? 0,
            arrangement_fee: product.arrangement_fee ?? product.arrangementFee ?? 0,
            interest_rate: product.interest_rate ?? product.rate ?? null,
            monthly_repayments: product.monthly_repayments ?? monthlyPaymentFromProduct ?? 0,
            total_repayable: product.total_repayable ?? 0,
            first_12_month_interest: product.first_12_month_interest ?? 0,
            contract_term_months: product.contract_term_months ?? product.termMonths ?? product.term_months ?? 0,
            ab_arrangement_fee:
              product.ab_arrangement_fee ?? product.abCalc?.arrangementFee ?? 0,
            ab_cost_over_product_term:
              product.ab_cost_over_product_term ??
              product.ab_cost_over_term ??
              product.abCalc?.costOverTerm ??
              0,
          } : {};

          const updateData = {
            ...applicationData,
            status: "Pre Enquiry",
            submitted_on: new Date().toISOString(),
            product_type: docProductType,
            loan_purpose: loanPurpose,
            repayment_method: repaymentMethod,
            repayment_type: repaymentTypeFromProduct ?? fullPayloadForBackend.repayment_type ?? repaymentMethod, // Document-level so backend returns it
            ...(shortlistedProductDocName && { shortlisted_product: shortlistedProductDocName }),
            ...ifFieldsFromProduct,
            application_payload: JSON.stringify({
              ...fullPayloadForBackend,
              borrowerType: actualBorrowerType,
              productType: productType,
              product_category: productType,
              productId: productId,
              loan_purpose: loanPurpose,
              loan_amount: loanAmountFromProduct ?? fullPayloadForBackend.loan_amount,
              property_value: propertyValueFromProduct ?? fullPayloadForBackend.property_value,
              repayment_type: repaymentTypeFromProduct ?? fullPayloadForBackend.repayment_type,
              cost_over_product_term: costOverProductTermFromProduct ?? fullPayloadForBackend.cost_over_product_term,
              monthly_payment: monthlyPaymentFromProduct ?? fullPayloadForBackend.monthly_payment,
              ...ifFieldsFromProduct,
              meta: meta // Preserve metadata for illustration
            }),
          };

          if (applicationData.parties && Array.isArray(applicationData.parties)) {
            updateData.parties = applicationData.parties;
          }

          delete updateData._allApplicants;

          await updateApplication(applicationName, updateData);

          // ✅ Attach temporary files to the existing application
          await attachTemporaryFiles(applicationName, appData);
        } else {
          // ✅ No application exists - CREATE it now (ONLY on final submit)
          console.log("=== CREATING NEW APPLICATION (EXPLICIT SUBMISSION) ===");

          const fullPayloadForBackend = appData?.fullPayload || appData?.payloadObj || appData || {};

          if (applicationData._allApplicants) {
            if (!fullPayloadForBackend.formData) fullPayloadForBackend.formData = {};
            if (!fullPayloadForBackend.formData.applicants) {
              fullPayloadForBackend.formData.applicants = applicationData._allApplicants;
            }
            if (!fullPayloadForBackend.btl) fullPayloadForBackend.btl = {};
            if (actualBorrowerType === "limited" && !fullPayloadForBackend.btl.limited) {
              fullPayloadForBackend.btl.limited = { applicants: applicationData._allApplicants };
            } else if (actualBorrowerType === "partnership" && !fullPayloadForBackend.btl.partnership) {
              fullPayloadForBackend.btl.partnership = { partners: applicationData._allApplicants };
            } else if (actualBorrowerType === "pension" && !fullPayloadForBackend.btl.pension) {
              fullPayloadForBackend.btl.pension = { members: applicationData._allApplicants };
            }
          }

          const loanAmountFromProduct = product.loan_amount ?? product.mortgageAmount ?? product.mortgage_amount ?? product.loan_amount;
          const propertyValueFromProduct = product.property_value ?? product.propertyValue ?? product._raw?.property_value ?? product._raw?.propertyValue;
          const repaymentTypeFromProduct = product.repayment_type ?? product.repaymentType ?? product.repayment_method ?? repaymentMethod;
          const costOverProductTermFromProduct = product.cost_over_product_term ?? product.total_cost ?? product.totalCost ?? fullPayloadForBackend.cost_over_product_term;
          const monthlyPaymentFromProduct = product.monthly_payment ?? product.monthlyPayment ?? product.monthlyCost ?? product.monthly_repayments ?? product.repayment ?? product._raw?.monthly_payment ?? product._raw?.monthly_repayments ?? fullPayloadForBackend.monthly_payment;

          const ifFieldsFromProduct = String(productType || "").toUpperCase() === "IF" ? {
            sector: product.sector ?? product._raw?.sector ?? null,
            projected_turnover: product.projected_turnover ?? product.projectedTurnover ?? 0,
            facility_required: product.facility_required ?? product.facilityRequired ?? product.headlineAmount ?? loanAmountFromProduct ?? 0,
            facility_type: (() => {
              const existing = String(product.facility_type ?? product.facilityType ?? "").trim();
              if (/^confidential$/i.test(existing)) return "Confidential";
              if (/^disclosure$|^disclosed$/i.test(existing)) return "Disclosure";
              const conf =
                product.confidential_facility ??
                product.confidentialFacility ??
                product.confidential ??
                0;
              return conf === true || conf === 1 || conf === "1" ? "Confidential" : "Disclosure";
            })(),
            net_advance: product.net_advance ?? product.netAdvance ?? 0,
            additional_borrowing_amount: product.additional_borrowing_amount ?? product.additionalBorrowingAmount ?? product.additionalLoanAmount ?? 0,
            additional_borrowing: product.additional_borrowing ?? ((product.additional_borrowing_amount || product.additionalLoanAmount) ? 1 : 0),
            confidential_facility: product.confidential_facility ?? product.confidentialFacility ?? 0,
            confidential: product.confidential ?? 0,
            disclosure: product.disclosure ?? 0,
            ip_percent: product.ip_percent ?? product.invoicePurchasePct ?? 0,
            discount_rate_percent: product.discount_rate_percent ?? product.discountRatePercent ?? 0,
            simple_service_fee: product.simple_service_fee ?? product.annualCostPct ?? 0,
            min_service_fee: product.min_service_fee ?? product.minServiceFee ?? 0,
            apr: product.apr ?? product.ratePct ?? product.rate ?? 0,
            arrangement_fee: product.arrangement_fee ?? product.arrangementFee ?? 0,
            interest_rate: product.interest_rate ?? product.rate ?? null,
            monthly_repayments: product.monthly_repayments ?? monthlyPaymentFromProduct ?? 0,
            total_repayable: product.total_repayable ?? 0,
            first_12_month_interest: product.first_12_month_interest ?? 0,
            contract_term_months: product.contract_term_months ?? product.termMonths ?? product.term_months ?? 0,
            ab_arrangement_fee:
              product.ab_arrangement_fee ?? product.abCalc?.arrangementFee ?? 0,
            ab_cost_over_product_term:
              product.ab_cost_over_product_term ??
              product.ab_cost_over_term ??
              product.abCalc?.costOverTerm ??
              0,
          } : {};

          const createPayload = {
            applicant: applicantEmail,
            status: "Pre Enquiry",
            submitted_on: new Date().toISOString(),
            product: productId,
            shortlisted_product: shortlistedProductDocName || undefined,
            lender: product.lenderName || "Unknown Lender",
            loan_purpose: loanPurpose,
            repayment_method: repaymentMethod,
            ...applicationData,
            ...ifFieldsFromProduct,
            product_type: docProductType,
            repayment_type: repaymentTypeFromProduct ?? fullPayloadForBackend.repayment_type ?? repaymentMethod, // Document-level so backend returns it (after spread so it wins)
            application_payload: JSON.stringify({
              ...fullPayloadForBackend,
              borrowerType: actualBorrowerType,
              productType: productType,
              product_category: productType,
              productId: productId,
              loan_purpose: loanPurpose,
              loan_amount: loanAmountFromProduct ?? fullPayloadForBackend.loan_amount,
              property_value: propertyValueFromProduct ?? fullPayloadForBackend.property_value,
              repayment_type: repaymentTypeFromProduct ?? fullPayloadForBackend.repayment_type,
              cost_over_product_term: costOverProductTermFromProduct ?? fullPayloadForBackend.cost_over_product_term,
              monthly_payment: monthlyPaymentFromProduct ?? fullPayloadForBackend.monthly_payment,
              ...ifFieldsFromProduct,
              meta: meta // Preserve metadata for illustration
            }),
          };

          if (applicationData.parties && Array.isArray(applicationData.parties)) {
            createPayload.parties = applicationData.parties;
          }

          delete createPayload._allApplicants;

          const created = await createApplication(createPayload);
          const newApplicationName = created?.name || created?.data?.name;

          // ✅ Attach temporary files to the newly created application
          if (newApplicationName) {
            await attachTemporaryFiles(newApplicationName, appData);
          }
        }
      }

      try { sessionStorage.removeItem(INTENT_KEY); } catch { }
      toast.success("Application submitted successfully!");
      navigate("/borrower/applications?submitted=1");
    } catch (err) {
      console.error("Submission failed", err);
      toast.error("Failed to submit application. Please try again.");
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };



  // Helper function to map form data to application fields
  const mapFormDataToApplication = (formData, borrowerType, productType) => {
    if (!formData) {
      console.warn("mapFormDataToApplication: formData is null/undefined");
      return {};
    }

    console.log("mapFormDataToApplication called with:", {
      formData,
      borrowerType,
      productType,
    });

    // Handle both flat form data and structured payload
    let dataToMap = formData;
    let innerFormData = null;

    // Check if data is nested in formData property
    if (formData.formData) {
      innerFormData = formData.formData;
      dataToMap = formData.formData;
    }
    // Check if data is nested in payloadObj.formData
    else if (formData.payloadObj) {
      if (formData.payloadObj.formData) {
        innerFormData = formData.payloadObj.formData;
        dataToMap = formData.payloadObj.formData;
      } else {
        dataToMap = { ...dataToMap, ...formData.payloadObj };
      }
    }

    // CRITICAL FIX: Define btlData and formDataType EARLY so helpers can access them
    const btlData =
      dataToMap.btl ||
      formData.btl ||
      formData.payloadObj?.btl ||
      dataToMap.if ||
      formData.if ||
      formData.payloadObj?.if;
    const formDataType = dataToMap.type || formData.type;
    const isInvoiceFinance = String(productType || "").toUpperCase() === "IF";

    // Helper to get from btlData based on type
    const getFromBtl = (field) => {
      if (!btlData) return undefined;
      // Check solo/individual
      if (btlData.solo && btlData.solo[field]) return btlData.solo[field];
      if (btlData.individual && btlData.individual[field]) return btlData.individual[field];
      return undefined;
    };

    // Always set ownership_type and owner_type based on borrowerType
    const ownershipTypeMap = {
      "individual": "Sole Proprietorship",
      "partnership": "Partnership",
      "limited": "Pvt Ltd",
      "pension": "Pension/Trust",
    };

    const mapped = {
      // Backend expects: "", "Sole Proprietorship", "Partnership", "Pvt Ltd", "Pension/Trust"
      ownership_type: ownershipTypeMap[borrowerType] || "",
      owner_type: "Borrower",
    };

    console.log("✓ Ownership mapping - borrowerType:", borrowerType, "→ ownership_type:", mapped.ownership_type);
    console.log("✓ Set owner_type:", mapped.owner_type);

    console.log("After extraction - dataToMap:", JSON.stringify(dataToMap, null, 2));

    // Map common fields that might be in formData (check both flat and nested)
    // Priority: btlData (nested specific) > dataToMap (extracted formData) > innerFormData > formData (original)
    const firstName = getFromBtl("firstName") || getFromBtl("first_name") || dataToMap.firstName || dataToMap.first_name || innerFormData?.firstName || innerFormData?.first_name || formData.firstName || formData.first_name;
    if (firstName) {
      mapped.first_name = String(firstName).trim();
    }

    const surname = getFromBtl("surname") || getFromBtl("last_name") || dataToMap.surname || dataToMap.last_name || innerFormData?.surname || innerFormData?.last_name || formData.surname || formData.last_name;
    if (surname) {
      mapped.surname = String(surname).trim();
    }

    const email = getFromBtl("email") || dataToMap.email || innerFormData?.email || formData.email;
    if (email) {
      mapped.email = String(email).trim();
    }

    const phone = getFromBtl("phone") || getFromBtl("phone_number") || dataToMap.phone || dataToMap.phone_number || innerFormData?.phone || innerFormData?.phone_number || formData.phone || formData.phone_number;
    if (phone) {
      mapped.phone_number = String(phone).trim();
    }

    const dateTime = dataToMap.date_time || dataToMap.dateTime || innerFormData?.date_time || innerFormData?.dateTime || formData.date_time || formData.dateTime;
    if (dateTime) {
      mapped.date_time = String(dateTime).trim();
    } else {
      // If no date_time provided, set to current date/time in backend format
      const now = new Date();
      // Format as "YYYY-MM-DD HH:MM:SS" (backend format)
      const formatted = now.toISOString().slice(0, 19).replace('T', ' ');
      mapped.date_time = formatted;
    }

    const credit = dataToMap.credit || dataToMap.credit_history || dataToMap.creditHistory || innerFormData?.credit || innerFormData?.credit_history || formData.credit_history || formData.creditHistory;
    if (credit) {
      mapped.credit_history = String(credit).trim();
    }

    // Handle props.number_of_properties_owned
    if (dataToMap.props !== undefined && typeof dataToMap.props === "object" && dataToMap.props.number_of_properties_owned !== undefined) {
      mapped.number_of_properties_owned = Number(dataToMap.props.number_of_properties_owned) || 0;
    } else if (dataToMap.props !== undefined && typeof dataToMap.props === "number") {
      // If props is a number directly (like in solo form where props: 1)
      mapped.number_of_properties_owned = Number(dataToMap.props) || 0;
    } else if (innerFormData?.props !== undefined) {
      if (typeof innerFormData.props === "object" && innerFormData.props.number_of_properties_owned !== undefined) {
        mapped.number_of_properties_owned = Number(innerFormData.props.number_of_properties_owned) || 0;
      } else if (typeof innerFormData.props === "number") {
        mapped.number_of_properties_owned = Number(innerFormData.props) || 0;
      }
    } else if (btlData?.solo?.props !== undefined) {
      if (typeof btlData.solo.props === "object" && btlData.solo.props.number_of_properties_owned !== undefined) {
        mapped.number_of_properties_owned = Number(btlData.solo.props.number_of_properties_owned) || 0;
      } else if (typeof btlData.solo.props === "number") {
        mapped.number_of_properties_owned = Number(btlData.solo.props) || 0;
      }
    } else if (formData.number_of_properties_owned !== undefined) {
      mapped.number_of_properties_owned = Number(formData.number_of_properties_owned) || 0;
    }

    // Handle agree1 and agree2 - check multiple paths including btl structure
    const agree1 = dataToMap.agree1 !== undefined ? dataToMap.agree1 :
      (innerFormData?.agree1 !== undefined ? innerFormData.agree1 :
        (formData.agree1 !== undefined ? formData.agree1 :
          (btlData?.solo_agree1 !== undefined ? btlData.solo_agree1 : undefined)));
    if (agree1 !== undefined) {
      mapped.provided_information_true = agree1 ? 1 : 0;
    }

    const agree2 = dataToMap.agree2 !== undefined ? dataToMap.agree2 :
      (innerFormData?.agree2 !== undefined ? innerFormData.agree2 :
        (formData.agree2 !== undefined ? formData.agree2 :
          (btlData?.solo_agree2 !== undefined ? btlData.solo_agree2 : undefined)));
    if (agree2 !== undefined) {
      mapped.property_repossessed = agree2 ? 1 : 0;
    }

    // Extract property address from multiple sources for all borrower types
    let propertyAddress = "";

    // Check solo/individual form
    if (borrowerType === "individual" || borrowerType === "solo" || formDataType === "solo") {
      propertyAddress = dataToMap.propertyAddress || dataToMap.property_address ||
        innerFormData?.propertyAddress || innerFormData?.property_address ||
        formData.propertyAddress || formData.property_address ||
        btlData?.solo?.propertyAddress || btlData?.solo?.property_address ||
        btlData?.individual?.propertyAddress || "";
    }
    // Check partnership form
    else if (borrowerType === "partnership" || formDataType === "partnership") {
      propertyAddress = dataToMap.propertyAddress || dataToMap.property_address ||
        innerFormData?.propertyAddress || innerFormData?.property_address ||
        formData.propertyAddress || formData.property_address ||
        btlData?.partnership?.propertyAddress || btlData?.partnership?.property_address || "";
    }
    // Check limited form
    else if (borrowerType === "limited" || formDataType === "limited") {
      propertyAddress = dataToMap.propertyAddress || dataToMap.property_address ||
        innerFormData?.propertyAddress || innerFormData?.property_address ||
        formData.propertyAddress || formData.property_address ||
        btlData?.limited?.propertyAddress || btlData?.limited?.property_address || "";
    }
    // Check pension form
    else if (borrowerType === "pension" || formDataType === "pension") {
      propertyAddress = dataToMap.propertyAddress || dataToMap.property_address ||
        innerFormData?.propertyAddress || innerFormData?.property_address ||
        formData.propertyAddress || formData.property_address ||
        btlData?.pension?.propertyAddress || btlData?.pension?.property_address || "";
    }

    // Map property address if found
    if (propertyAddress) {
      mapped.property_address = String(propertyAddress).trim();
      console.log("  → Set property_address:", mapped.property_address);
    }

    // btlData and formDataType are already defined at top
    // Limited Company fields
    if (borrowerType === "limited" || formDataType === "limited") {
      let company = null;

      // Try nested structure first (btl.limited.company)
      if (btlData?.limited?.company) {
        company = btlData.limited.company;
        console.log("✓ Found company in btlData.limited.company");
      }
      // Try flat structure (dataToMap.company) - this is the main path for Wizard onSubmit
      else if (dataToMap.company) {
        company = dataToMap.company;
        console.log("✓ Found company in dataToMap.company");
      }
      // Try innerFormData.company
      else if (innerFormData?.company) {
        company = innerFormData.company;
        console.log("✓ Found company in innerFormData.company");
      }
      // Try formData.company directly (if formData is flat)
      else if (formData.company && !formData.formData) {
        company = formData.company;
        console.log("✓ Found company in formData.company (flat)");
      }
      // Try formData.formData.company (if formData is nested)
      else if (formData.formData?.company) {
        company = formData.formData.company;
        console.log("✓ Found company in formData.formData.company");
      }

      if (company) {
        console.log("Company object:", JSON.stringify(company, null, 2));
        if (company.name) {
          mapped.company_name = String(company.name).trim();
          console.log("  → Set company_name:", mapped.company_name);
        }
        if (company.regNo) {
          mapped.company_registration_number = String(company.regNo).trim();
          console.log("  → Set company_registration_number:", mapped.company_registration_number);
        }
        if (company.phone) {
          mapped.company_telephone_number = String(company.phone).trim();
          console.log("  → Set company_telephone_number:", mapped.company_telephone_number);
        }
        if (company.email) {
          mapped.company_email = String(company.email).trim();
          console.log("  → Set company_email:", mapped.company_email);
        }
        console.log("✓ Company fields mapped successfully");
      } else {
        console.warn("✗ No company data found for limited borrower type");
        console.warn("  - dataToMap:", dataToMap);
        console.warn("  - innerFormData:", innerFormData);
        console.warn("  - formData:", formData);
      }

      // Handle props (number_of_properties_owned) for limited co
      // Check multiple paths: btlData.limited.props, dataToMap.props, innerFormData.props
      if (btlData?.limited?.props !== undefined && btlData.limited.props !== null && btlData.limited.props !== "") {
        mapped.number_of_properties_owned = Number(btlData.limited.props) || 0;
        console.log("  → Set number_of_properties_owned from btlData.limited.props:", mapped.number_of_properties_owned);
      } else if (dataToMap.props !== undefined && dataToMap.props !== null && dataToMap.props !== "") {
        mapped.number_of_properties_owned = Number(dataToMap.props) || 0;
        console.log("  → Set number_of_properties_owned from dataToMap.props:", mapped.number_of_properties_owned);
      } else if (innerFormData?.props !== undefined && innerFormData.props !== null && innerFormData.props !== "") {
        mapped.number_of_properties_owned = Number(innerFormData.props) || 0;
        console.log("  → Set number_of_properties_owned from innerFormData.props:", mapped.number_of_properties_owned);
      } else if (formData.props !== undefined && formData.props !== null && formData.props !== "") {
        mapped.number_of_properties_owned = Number(formData.props) || 0;
        console.log("  → Set number_of_properties_owned from formData.props:", mapped.number_of_properties_owned);
      }
    }

    // Partnership fields - extract agree1 and agree2 (will be handled after applicants are found)
    // This section is kept for future partnership-specific field mapping if needed
    if (borrowerType === "partnership" || formDataType === "partnership") {
      // agree1 and agree2 extraction is handled after applicants are found
      // to ensure we check all possible locations
      console.log("Partnership borrower type detected - agree1/agree2 will be extracted after applicants");
    }

    // Pension/Trust fields
    if (borrowerType === "pension" || formDataType === "pension") {
      if (btlData?.pension) {
        const pension = btlData.pension;
        const scheme = pension.scheme || {};
        if (scheme.name || pension.trustName) mapped.trust_name = scheme.name || pension.trustName;
        if (scheme.number || pension.trustNumber) mapped.trust_number = scheme.number || pension.trustNumber;
        // Map properties owned from scheme.props
        if (scheme.props !== undefined && scheme.props !== null && scheme.props !== "") {
          mapped.number_of_properties_owned = Number(scheme.props) || 0;
        }
      }
      // Try flat structure
      else if (dataToMap.trustName || dataToMap.trustNumber || dataToMap.props !== undefined) {
        if (dataToMap.trustName) mapped.trust_name = dataToMap.trustName;
        if (dataToMap.trustNumber) mapped.trust_number = dataToMap.trustNumber;
        if (dataToMap.props !== undefined && dataToMap.props !== null && dataToMap.props !== "") {
          mapped.number_of_properties_owned = Number(dataToMap.props) || 0;
        }
      }
      // Also check scheme object directly
      else if (dataToMap.scheme) {
        const scheme = dataToMap.scheme;
        if (scheme.name) mapped.trust_name = scheme.name;
        if (scheme.number) mapped.trust_number = scheme.number;
        if (scheme.props !== undefined && scheme.props !== null && scheme.props !== "") {
          mapped.number_of_properties_owned = Number(scheme.props) || 0;
        }
      }
    }

    // Map applicants data - use first applicant for main fields if not already set
    // Check multiple paths including nested btl structures
    // NOTE: Partnership forms use "partners" instead of "applicants"
    let applicants = null;

    // Priority 1: Check dataToMap.partners (for Partnership forms - most common structure)
    if (dataToMap.partners && Array.isArray(dataToMap.partners) && dataToMap.partners.length > 0) {
      applicants = dataToMap.partners;
      console.log("✓ Found partners/applicants in dataToMap.partners");
    }
    // Priority 2: Check innerFormData.partners (for Partnership forms)
    else if (innerFormData?.partners && Array.isArray(innerFormData.partners) && innerFormData.partners.length > 0) {
      applicants = innerFormData.partners;
      console.log("✓ Found partners/applicants in innerFormData.partners");
    }
    // Priority 3: Check formData.formData.partners (for Partnership forms)
    else if (formData.formData?.partners && Array.isArray(formData.formData.partners) && formData.formData.partners.length > 0) {
      applicants = formData.formData.partners;
      console.log("✓ Found partners/applicants in formData.formData.partners");
    }
    // Priority 4: Check btlData.limited.applicants (for Limited forms)
    else if (btlData?.limited?.applicants && Array.isArray(btlData.limited.applicants) && btlData.limited.applicants.length > 0) {
      applicants = btlData.limited.applicants;
      console.log("✓ Found applicants in btlData.limited.applicants");
    }
    // Priority 5: Check btlData.partnership.partners (for Partnership forms - nested btl structure)
    else if (btlData?.partnership?.partners && Array.isArray(btlData.partnership.partners) && btlData.partnership.partners.length > 0) {
      applicants = btlData.partnership.partners;
      console.log("✓ Found partners/applicants in btlData.partnership.partners");
    }
    // Priority 6: Check dataToMap.applicants
    else if (dataToMap.applicants && Array.isArray(dataToMap.applicants) && dataToMap.applicants.length > 0) {
      applicants = dataToMap.applicants;
      console.log("✓ Found applicants in dataToMap.applicants");
    }
    // Priority 7: Check innerFormData.applicants
    else if (innerFormData?.applicants && Array.isArray(innerFormData.applicants) && innerFormData.applicants.length > 0) {
      applicants = innerFormData.applicants;
      console.log("✓ Found applicants in innerFormData.applicants");
    }
    // Priority 8: Check formData.applicants (if formData is flat)
    else if (formData.applicants && Array.isArray(formData.applicants) && formData.applicants.length > 0 && !formData.formData) {
      applicants = formData.applicants;
      console.log("✓ Found applicants in formData.applicants (flat)");
    }
    // Priority 9: Check formData.formData.applicants (if formData is nested)
    else if (formData.formData?.applicants && Array.isArray(formData.formData.applicants) && formData.formData.applicants.length > 0) {
      applicants = formData.formData.applicants;
      console.log("✓ Found applicants in formData.formData.applicants");
    }
    // Priority 10: Check formData.payloadObj structure (if it exists)
    else if (formData.payloadObj?.formData?.applicants && Array.isArray(formData.payloadObj.formData.applicants) && formData.payloadObj.formData.applicants.length > 0) {
      applicants = formData.payloadObj.formData.applicants;
      console.log("✓ Found applicants in formData.payloadObj.formData.applicants");
    }
    // Priority 11: Check formData.payloadObj.formData.partners (for Partnership forms)
    else if (formData.payloadObj?.formData?.partners && Array.isArray(formData.payloadObj.formData.partners) && formData.payloadObj.formData.partners.length > 0) {
      applicants = formData.payloadObj.formData.partners;
      console.log("✓ Found partners/applicants in formData.payloadObj.formData.partners");
    }
    // Priority 12: Check formData.payloadObj.btl.limited.applicants
    else if (formData.payloadObj?.btl?.limited?.applicants && Array.isArray(formData.payloadObj.btl.limited.applicants) && formData.payloadObj.btl.limited.applicants.length > 0) {
      applicants = formData.payloadObj.btl.limited.applicants;
      console.log("✓ Found applicants in formData.payloadObj.btl.limited.applicants");
    }
    // Priority 13: Check formData.payloadObj.btl.partnership.partners
    else if (formData.payloadObj?.btl?.partnership?.partners && Array.isArray(formData.payloadObj.btl.partnership.partners) && formData.payloadObj.btl.partnership.partners.length > 0) {
      applicants = formData.payloadObj.btl.partnership.partners;
      console.log("✓ Found partners/applicants in formData.payloadObj.btl.partnership.partners");
    }
    // Priority 14: Check btlData.pension.members (for Pension/Trust forms)
    else if (btlData?.pension?.members && Array.isArray(btlData.pension.members) && btlData.pension.members.length > 0) {
      applicants = btlData.pension.members;
      console.log("✓ Found members/applicants in btlData.pension.members");
    }
    // Priority 15: Check dataToMap.members (for Pension/Trust forms - flat structure)
    else if (dataToMap.members && Array.isArray(dataToMap.members) && dataToMap.members.length > 0) {
      applicants = dataToMap.members;
      console.log("✓ Found members/applicants in dataToMap.members");
    }
    // Priority 16: Check innerFormData.members (for Pension/Trust forms)
    else if (innerFormData?.members && Array.isArray(innerFormData.members) && innerFormData.members.length > 0) {
      applicants = innerFormData.members;
      console.log("✓ Found members/applicants in innerFormData.members");
    }
    // Priority 17: Check formData.formData.members (for Pension/Trust forms)
    else if (formData.formData?.members && Array.isArray(formData.formData.members) && formData.formData.members.length > 0) {
      applicants = formData.formData.members;
      console.log("✓ Found members/applicants in formData.formData.members");
    }
    // Priority 18: Check formData.payloadObj.btl.pension.members (for Pension/Trust forms)
    else if (formData.payloadObj?.btl?.pension?.members && Array.isArray(formData.payloadObj.btl.pension.members) && formData.payloadObj.btl.pension.members.length > 0) {
      applicants = formData.payloadObj.btl.pension.members;
      console.log("✓ Found members/applicants in formData.payloadObj.btl.pension.members");
    }

    if (applicants && applicants.length > 0) {
      console.log("Applicants array found with", applicants.length, "applicant(s):", JSON.stringify(applicants, null, 2));
      const firstApplicant = applicants[0];

      // Map first applicant to main fields if not already set
      if (!mapped.first_name && firstApplicant.firstName) {
        mapped.first_name = String(firstApplicant.firstName).trim();
        console.log("  → Set first_name from first applicant:", mapped.first_name);
      }
      if (!mapped.surname && firstApplicant.surname) {
        mapped.surname = String(firstApplicant.surname).trim();
        console.log("  → Set surname from first applicant:", mapped.surname);
      }
      if (!mapped.email && firstApplicant.email) {
        mapped.email = String(firstApplicant.email).trim();
        console.log("  → Set email from first applicant:", mapped.email);
      }
      if (!mapped.phone_number && firstApplicant.phone) {
        mapped.phone_number = String(firstApplicant.phone).trim();
        console.log("  → Set phone_number from first applicant:", mapped.phone_number);
      }

      // For partnership forms, extract credit_history and number_of_properties_owned from first partner
      // Partners have "credit" and "props" fields instead of "credit_history" and "number_of_properties_owned"
      if (borrowerType === "partnership" || formDataType === "partnership") {
        const firstPartner = applicants[0];
        // Map credit from first partner to credit_history
        if (!mapped.credit_history && firstPartner.credit) {
          mapped.credit_history = String(firstPartner.credit).trim();
          console.log("  → Set credit_history from first partner:", mapped.credit_history);
        }
        // Map props from first partner to number_of_properties_owned
        if (mapped.number_of_properties_owned === undefined && firstPartner.props !== undefined) {
          mapped.number_of_properties_owned = Number(firstPartner.props) || 0;
          console.log("  → Set number_of_properties_owned from first partner:", mapped.number_of_properties_owned);
        }

        // Extract agree1 and agree2 from partnership structure
        let partnershipAgree1 = null;
        let partnershipAgree2 = null;

        if (btlData?.partnership) {
          partnershipAgree1 = btlData.partnership.agree1;
          partnershipAgree2 = btlData.partnership.agree2;
          console.log("✓ Found agree1/agree2 in btlData.partnership");
        }
        // Also check dataToMap for partnership agree fields
        else if (dataToMap.agree1 !== undefined || dataToMap.agree2 !== undefined) {
          partnershipAgree1 = dataToMap.agree1;
          partnershipAgree2 = dataToMap.agree2;
          console.log("✓ Found agree1/agree2 in dataToMap");
        }
        // Also check innerFormData
        else if (innerFormData?.agree1 !== undefined || innerFormData?.agree2 !== undefined) {
          partnershipAgree1 = innerFormData.agree1;
          partnershipAgree2 = innerFormData.agree2;
          console.log("✓ Found agree1/agree2 in innerFormData");
        }
        // Also check formData.formData (for nested structure)
        else if (formData.formData?.agree1 !== undefined || formData.formData?.agree2 !== undefined) {
          partnershipAgree1 = formData.formData.agree1;
          partnershipAgree2 = formData.formData.agree2;
          console.log("✓ Found agree1/agree2 in formData.formData");
        }

        // Map agree1 to provided_information_true if not already set
        if (partnershipAgree1 !== undefined && mapped.provided_information_true === undefined) {
          mapped.provided_information_true = partnershipAgree1 ? 1 : 0;
          console.log("  → Set provided_information_true from partnership agree1:", mapped.provided_information_true);
        }

        // Map agree2 to property_repossessed if not already set
        if (partnershipAgree2 !== undefined && mapped.property_repossessed === undefined) {
          mapped.property_repossessed = partnershipAgree2 ? 1 : 0;
          console.log("  → Set property_repossessed from partnership agree2:", mapped.property_repossessed);
        }
      }

      // Store all applicants in the mapped object so they can be included in application_payload
      // This ensures all applicants are preserved when the payload is sent to the backend
      mapped._allApplicants = applicants;
      console.log("✓ Stored all", applicants.length, "applicant(s) in mapped._allApplicants for application_payload");

      // Convert applicants to parties array format for backend (child table)
      // Backend expects parties array with: party_role, sequence_no, first_name, surname, email, phone_number
      // Backend valid party_role values: "Additional Applicant", "Partner", "Director/Shareholder", "Trustee/Member"
      // Determine the correct party_role based on borrower type
      let partyRole = "Additional Applicant"; // Default role
      if (borrowerType === "partnership" || formDataType === "partnership") {
        partyRole = "Partner"; // For partnership forms, use "Partner"
      } else if (borrowerType === "pension" || formDataType === "pension") {
        partyRole = "Trustee/Member"; // For pension/trust forms, use "Trustee/Member"
      } else if (borrowerType === "limited" || formDataType === "limited") {
        partyRole = "Director/Shareholder"; // For limited company, use "Director/Shareholder" (directors/owners)
      }

      const parties = applicants.map((applicant, index) => {
        const party = {
          party_role: partyRole,
          sequence_no: index + 1, // Sequence starts at 1
          first_name: applicant.firstName || applicant.first_name || "",
          surname: applicant.surname || "",
        };

        // Add optional fields if available
        if (applicant.email) {
          party.email = applicant.email;
        }
        if (applicant.phone || applicant.phone_number) {
          party.phone_number = applicant.phone || applicant.phone_number;
        }

        return party;
      });

      // Set parties array in mapped object (backend expects this for the child table)
      mapped.parties = parties;
      console.log("✓ Converted", applicants.length, "applicant(s) to parties array:", JSON.stringify(parties, null, 2));
      console.log("✓ Applicant fields mapped successfully");
    } else {
      // No applicants found - set empty parties array
      mapped.parties = [];
      console.warn("✗ No applicants found in any expected location");
      console.warn("  - dataToMap.partners:", dataToMap.partners);
      console.warn("  - innerFormData?.partners:", innerFormData?.partners);
      console.warn("  - formData.formData?.partners:", formData.formData?.partners);
      console.warn("  - btlData.limited.applicants:", btlData?.limited?.applicants);
      console.warn("  - btlData.partnership.partners:", btlData?.partnership?.partners);
      console.warn("  - btlData.pension.members:", btlData?.pension?.members);
      console.warn("  - dataToMap.applicants:", dataToMap.applicants);
      console.warn("  - dataToMap.members:", dataToMap.members);
      console.warn("  - innerFormData?.applicants:", innerFormData?.applicants);
      console.warn("  - formData.applicants:", formData.applicants);
      console.warn("  - formData.formData?.applicants:", formData.formData?.applicants);
      console.warn("  - formData.payloadObj:", formData.payloadObj);
    }

    // Map document fields (file names) - check for uploads in both nested and flat structures
    // First check flat structure (formData.uploads or dataToMap.uploads)
    let uploads = dataToMap.uploads || formData.uploads;

    // If not found, check nested structure (btlData / if payload aliased as btlData)
    if (!uploads && btlData) {
      if (btlData.solo?.uploads || btlData.individual?.uploads || btlData.solo_uploads) {
        uploads = btlData.solo?.uploads || btlData.individual?.uploads || btlData.solo_uploads;
      } else if (btlData.limited?.uploads) {
        uploads = btlData.limited.uploads;
      } else if (btlData.partnership?.uploads) {
        uploads = btlData.partnership.uploads;
      } else if (btlData.pension?.uploads) {
        uploads = btlData.pension.uploads;
      }
    }

    // Explicit IF payload path (when both btl and if exist, prefer if uploads for IF apps)
    if (isInvoiceFinance) {
      const ifData = dataToMap.if || formData.if || formData.payloadObj?.if || formData.fullPayload?.if;
      const ifUploadsCandidate =
        ifData?.solo?.uploads ||
        ifData?.limited?.uploads ||
        ifData?.partnership?.uploads ||
        ifData?.pension?.uploads;
      if (ifUploadsCandidate && Object.keys(ifUploadsCandidate).length > 0) {
        uploads = ifUploadsCandidate;
      }
    }

    // Helper function to extract file names from file objects
    const getFileNames = (files) => {
      if (!Array.isArray(files) || files.length === 0) return "";
      return files.map(u => {
        // Priority: file_name > name > file.name (from original file) > file_url (as fallback)
        return u.file_name || u.name || (u.file && u.file.name) || (typeof u === 'string' ? u.split('/').pop() : 'File');
      }).filter(name => name && name !== 'File').join(", ");
    };

    // Map uploads if found - use new bucket keys and store file names instead of URLs
    if (uploads) {
      // Property Accounts - check both old and new keys
      const propertyAccounts = uploads.borrower_accounts || uploads.propertyAccounts || [];
      if (Array.isArray(propertyAccounts) && propertyAccounts.length > 0) {
        mapped.property_accounts = getFileNames(propertyAccounts);
      }

      // 3 months bank statement - check both old and new keys
      const bankStatements = uploads.borrower_bank_statements || uploads.bankStatement || [];
      if (Array.isArray(bankStatements) && bankStatements.length > 0) {
        mapped["3_months_bank_statement"] = getFileNames(bankStatements);
      }

      // Property Portfolio - check both old and new keys
      const propertyPortfolio = uploads.borrower_portfolio || uploads.propertyPortfolio || [];
      if (Array.isArray(propertyPortfolio) && propertyPortfolio.length > 0) {
        mapped.property_portfolio = getFileNames(propertyPortfolio);
      }

      // Assets & Liability Statement - check both old and new keys
      const assetsLiability = uploads.app_assets_liability || uploads.assetsLiability || [];
      if (Array.isArray(assetsLiability) && assetsLiability.length > 0) {
        mapped.assets__liability_statement = getFileNames(assetsLiability);
      }

      // Identification - check both old and new keys
      const identification = uploads.app_id || uploads.identification || [];
      if (Array.isArray(identification) && identification.length > 0) {
        mapped.identification = getFileNames(identification);
      }

      // 3 months personal bank statements - check both old and new keys
      const personalBankStatements = uploads.app_personal_bank || uploads.personalBankStatements || [];
      if (Array.isArray(personalBankStatements) && personalBankStatements.length > 0) {
        mapped["3_months_personal_bank_statements"] = getFileNames(personalBankStatements);
      }

      // Self Employed documents - store file names
      const selfEmployed = uploads.app_self_employed || [];
      if (Array.isArray(selfEmployed) && selfEmployed.length > 0) {
        const fileNames = getFileNames(selfEmployed);
        if (fileNames) {
          // Store document names in self_employed field (backend expects file names/paths)
          mapped.self_employed = fileNames;
        }
      }

      // Employed documents - store file names
      const employed = uploads.app_employed || [];
      if (Array.isArray(employed) && employed.length > 0) {
        const fileNames = getFileNames(employed);
        if (fileNames) {
          // Store document names in employed field (backend expects file names/paths)
          mapped.employed = fileNames;
        }
      }

      const ifUploads = normalizeIfUploads(uploads || {});
      const ifAnnual =
        ifUploads.if_full_annual_business_accounts ||
        ifUploads.if_annual_accounts ||
        [];
      if (ifAnnual.length) {
        mapped.if_full_annual_business_accounts = getFileNames(ifAnnual);
      }
      const ifMgmt = ifUploads.if_management_accounts || [];
      if (ifMgmt.length) mapped.if_management_accounts = getFileNames(ifMgmt);
      const ifBank =
        ifUploads.if_3_months_business_bank_statement ||
        ifUploads.if_business_bank ||
        [];
      if (ifBank.length) {
        mapped.if_3_months_business_bank_statement = getFileNames(ifBank);
      }
      const ifDebtor = ifUploads.if_aged_debtors || ifUploads.if_aged_debtor || [];
      if (ifDebtor.length) mapped.if_aged_debtors = getFileNames(ifDebtor);
      const ifCreditor =
        ifUploads.if_aged_creditors || ifUploads.if_aged_creditor || [];
      if (ifCreditor.length) mapped.if_aged_creditors = getFileNames(ifCreditor);
      const ifId = ifUploads.if_id_passport || ifUploads.app_id || [];
      if (ifId.length) mapped.if_id_passport = getFileNames(ifId);
    }

    if (isInvoiceFinance) {
      delete mapped.property_address;
      delete mapped.number_of_properties_owned;
    }

    console.log("=== FINAL MAPPED RESULT ===");
    console.log("Mapped fields:", JSON.stringify(mapped, null, 2));
    console.log("Field count:", Object.keys(mapped).length);
    console.log("Fields present:", Object.keys(mapped));
    console.log("==========================");

    // Remove only undefined and null values, but keep empty strings for backend fields
    // Backend fields should be explicitly set even if empty
    // Special handling for parties array - keep it even if empty array
    const cleaned = {};
    for (const [key, value] of Object.entries(mapped)) {
      if (key === 'parties') {
        // Always include parties array (even if empty) as backend expects it
        cleaned[key] = Array.isArray(value) ? value : [];
      } else if (value !== undefined && value !== null) {
        cleaned[key] = value;
      }
    }

    console.log("=== CLEANED MAPPED RESULT ===");
    console.log("Cleaned fields:", JSON.stringify(cleaned, null, 2));
    console.log("Field count:", Object.keys(cleaned).length);
    console.log("All fields:", Object.keys(cleaned).join(", "));
    console.log("============================");

    return cleaned;
  };

  const steps = [
    { key: "borrower", label: "Borrower type" },
    { key: "form", label: "Application Form" },
    { key: "review", label: "Review & Submit" },
  ];

  const goPrev = () => {
    // ✅ When going back, preserve all state - don't trigger any submission
    setStep((s) => Math.max(0, s - 1));
    // draftApp state is already preserved in parent component
  };
  // Only allow jumping BACK via the stepper; forward disabled
  const jumpTo = (idx) => {
    if (idx < step) {
      // When jumping back, ensure we don't trigger any submission
      setStep(idx);
    }
  };

  return (
    <div className="space-y-6 px-2 sm:px-0 max-w-full min-w-0">
      {/* Centered Stepper (replaces hero) */}
      <div className="pt-1">
        <Stepper
          steps={steps}
          current={step}
          onStepChange={jumpTo}              // only backwards
          className="mb-2 w-full max-w-3xl mx-auto"
        />
      </div>

      {/* Step content */}
      {step === 0 && (
        <BorrowerTypeStep
          uiType={uiType}
          setUiType={(t) => {
            setUiType(t);
            if (t) setStep(1);
          }}
          navigate={navigate}
          productType={productType}
        />
      )}

      {step === 1 && borrowerType && (
        <ApplicationFormStep
          productType={productType}
          productId={productId}
          borrowerType={borrowerType}
          loanPurpose={loanPurpose}
          initialPayload={draftApp ? (() => {
            // Ensure structure matches what forms expect
            // Forms expect: initialPayload.btl.solo, initialPayload.btl.limited, etc.
            // Priority: payloadObj > fullPayload > draftApp
            const payloadObj = draftApp.payloadObj || draftApp.fullPayload?.payloadObj;
            const fullPayload = payloadObj || draftApp.fullPayload || draftApp.payloadObj || draftApp;
            const btlStructure = fullPayload?.btl || draftApp.btl || {};

            // If payloadObj exists, use it directly (it has the correct structure)
            if (payloadObj && payloadObj.btl) {
              return payloadObj;
            }

            // Otherwise, reconstruct the structure
            return {
              ...fullPayload,
              btl: {
                ...btlStructure,
                // Ensure uploads are preserved in the correct location for each form type
                ...(borrowerType === "individual" || borrowerType === "solo" ? {
                  solo: {
                    ...btlStructure.solo,
                    firstName: draftApp.firstName || btlStructure.solo?.firstName || "",
                    surname: draftApp.surname || btlStructure.solo?.surname || "",
                    phone: draftApp.phone || btlStructure.solo?.phone || "",
                    email: draftApp.email || btlStructure.solo?.email || "",
                    credit: draftApp.credit || btlStructure.solo?.credit || "",
                    props: draftApp.props || btlStructure.solo?.props || "",
                  },
                  solo_uploads: draftApp.uploads || btlStructure.solo_uploads || btlStructure.solo?.uploads || fullPayload?.btl?.solo_uploads || {},
                } : {}),
                ...(borrowerType === "limited" ? {
                  limited: {
                    ...btlStructure.limited,
                    company: draftApp.company || btlStructure.limited?.company || {},
                    applicants: draftApp.applicants || btlStructure.limited?.applicants || [],
                    uploads: draftApp.uploads || btlStructure.limited?.uploads || {},
                  },
                } : {}),
                ...(borrowerType === "partnership" ? {
                  partnership: {
                    ...btlStructure.partnership,
                    partners: draftApp.partners || btlStructure.partnership?.partners || [],
                    uploads: draftApp.uploads || btlStructure.partnership?.uploads || {},
                  },
                } : {}),
                ...(borrowerType === "pension" ? {
                  pension: {
                    ...btlStructure.pension,
                    scheme: draftApp.scheme || btlStructure.pension?.scheme || {},
                    members: draftApp.members || btlStructure.pension?.members || [],
                    uploads: draftApp.uploads || btlStructure.pension?.uploads || {},
                  },
                } : {}),
              },
              formData: draftApp.formData || fullPayload?.formData || {},
            };
          })() : null} // Pass draftApp as initialPayload to preserve form data when going back
          applicationName={existingApplicationName} // Pass applicationName to preserve it
          isSubmitting={isSubmitting}
          onBack={goPrev}
          onComplete={(payload) => {
            // Store the applicationName from Wizard if it exists
            if (payload?.applicationName) {
              setExistingApplicationName(payload.applicationName);
            }

            // Get the full payload structure (this contains btl structure with all form data)
            // Priority: payloadObj > fullPayload > payload
            const payloadObj = payload?.payloadObj || payload?.fullPayload?.payloadObj;
            const fullPayload = payloadObj || payload?.fullPayload || payload?.payloadObj || payload;

            const mergedDraft = {
              ...payload,
              productType,
              productId,
              borrowerType,
              // Preserve the full payload structure (prioritize payloadObj if it exists)
              payloadObj: payloadObj || fullPayload,
              fullPayload: fullPayload,
              // Also ensure btl structure is at top level for forms to use
              btl: fullPayload?.btl || payload?.btl,
              formData: fullPayload?.formData || payload?.formData,
              // Also preserve flat data for backward compatibility
              ...(payload.type === "solo" || payload.type === "individual" ? {
                firstName: payload.firstName,
                surname: payload.surname,
                phone: payload.phone,
                email: payload.email,
                credit: payload.credit,
                props: payload.props,
                uploads: payload.uploads,
              } : {}),
              ...(payload.type === "limited" ? {
                company: payload.company,
                applicants: payload.applicants,
                uploads: payload.uploads,
              } : {}),
              ...(payload.type === "partnership" ? {
                partners: payload.partners,
                uploads: payload.uploads,
              } : {}),
              ...(payload.type === "pension" ? {
                scheme: payload.scheme,
                members: payload.members,
                uploads: payload.uploads,
              } : {}),
            };

            setDraftApp(mergedDraft);

            if (payload?.submitFromReview) {
              handleFinalSubmit(
                { agree1: payload.agree1 === true, agree2: payload.agree2 === true },
                mergedDraft
              );
              return;
            }

            setStep(2);
          }}
        />
      )}

      {step === 2 && (
        <ReviewSubmitStep
          data={draftApp}
          onBack={() => {
            // Go back to form step (step 1), preserving all form data
            // The form data is already in draftApp state, which will be passed to Wizard as initialPayload
            setStep(1);
          }}
          onEditForm={() => {
            // Go back to form step, but keep the review step accessible
            setStep(1);
          }}
          onSubmit={handleFinalSubmit}
          isSubmitting={isSubmitting}
          onDataUpdate={(updatedData) => {
            // Update draftApp state with the new data
            setDraftApp(prev => ({
              ...prev,
              ...updatedData,
              formData: updatedData.formData || prev?.formData,
              payloadObj: {
                ...prev?.payloadObj,
                formData: updatedData.formData || prev?.payloadObj?.formData,
              },
              fullPayload: {
                ...prev?.fullPayload,
                formData: updatedData.formData || prev?.fullPayload?.formData,
                btl: updatedData.btlData || prev?.fullPayload?.btl,
              },
              btl: updatedData.btlData || prev?.btl,
            }));
          }}
        />
      )}
    </div>
  );
}

/* ------------------------ Step 0: Borrower Type ------------------------ */
function BorrowerTypeStep({ uiType, setUiType, navigate, productType }) {
  const borrowerTypes = [
    { id: "solo", name: "Sole Trader", icon: "👤", description: "Individual self-employed trader" },
    { id: "partnership", name: "Partnership", icon: "🤝", description: "Business partnership" },
    { id: "limited", name: "Ltd. Co. & LLP", icon: "🏢", description: "Limited company or LLP" },
    { id: "pension", name: "Pension/Trusts", icon: "🏦", description: "Pension or trust fund" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(shortlistPathForCategory(productType))}
          className="group flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-sky-600 transition-colors"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white border border-slate-200 group-hover:border-sky-200 group-hover:bg-sky-50 transition-all">
            <ArrowLeft className="w-4 h-4" />
          </div>
          Back to Shortlisted Products
        </button>
      </div>

      <p className="text-sm md:text-base font-medium text-slate-700">
        Please Choose your borrower type
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {borrowerTypes.map((t) => (
          <Card key={t.id} className={`p-0 overflow-hidden min-w-0 ${uiType === t.id ? "ring-2 ring-sky-400" : ""}`}>
            <button
              type="button"
              onClick={() => setUiType(t.id)}
              className="w-full text-left p-4 sm:p-5 hover:bg-slate-50 transition group cursor-pointer"
            >
              <div className="text-4xl mb-2">{t.icon}</div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                {t.name}
              </h3>
              <p className="text-sm text-slate-600 leading-snug">{t.description}</p>
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ------------------ Step 1: Application Form (Wizard) ------------------ */
function ApplicationFormStep({ productType, productId, borrowerType, loanPurpose, initialPayload, applicationName, onBack, onComplete, isSubmitting }) {
  return (
    <div className="space-y-4">
      {/* Left-aligned Back only — NO "Application Form" heading */}
      <div className="flex items-center">
        <button
          className="text-sm font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-2"
          onClick={onBack}
        >
          ← Back
        </button>
      </div>

      <Wizard
        productType={productType}
        productId={productId}
        borrowerType={borrowerType}
        loanPurpose={loanPurpose}
        applicationName={applicationName} // Pass applicationName to preserve it
        isSubmitting={isSubmitting}
        showStepper={false}     // keep Wizard's internal stepper hidden
        skipReviewStep={true}   // Skip Wizard's review step - form has internal review step
        onBack={onBack}
        onSubmit={(payload) => {
          onComplete?.(payload);
        }}
        // Pass initialPayload to Wizard to restore form data when going back from review
        initialPayload={initialPayload}
      />
    </div>
  );
}

/* ----------------------- Step 2: Review & Submit ----------------------- */
function ReviewSubmitStep({ data, onBack, onEditForm, onSubmit, isSubmitting, onDataUpdate }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [localData, setLocalData] = useState(data); // Local state to hold updated data
  const [declaration1, setDeclaration1] = useState(false);
  const [declaration2, setDeclaration2] = useState(false);
  const applicationName = data?.applicationName;

  // Update local data when data prop changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-sm text-slate-600">No application data to review yet.</p>
        <div className="mt-4">
          <button
            className="rounded-xl px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50"
            onClick={onBack}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // Use localData instead of data to reflect updates
  const currentData = localData || data;
  const { productType, productId, borrowerType, ...restData } = currentData || {};
  const niceBorrower =
    { individual: "Sole Trader (Individual)", partnership: "Partnership", limited: "Ltd Co / LLP", pension: "Pension/Trust" }[borrowerType] || borrowerType;

  // Try to parse application_payload if it's a string
  let parsedPayload = null;
  if (restData.application_payload && typeof restData.application_payload === 'string') {
    try {
      parsedPayload = JSON.parse(restData.application_payload);
    } catch (e) {
      console.warn("Failed to parse application_payload:", e);
    }
  }

  // Extract form data from various possible structures
  // Check parsed application_payload first, then other locations
  // Note: For Wizard, formData might be at restData level directly (not nested)
  const formData = parsedPayload?.formData || restData.formData || restData.payloadObj?.formData || restData.fullPayload?.formData || restData;
  const btlData =
    parsedPayload?.btl ||
    restData.btl ||
    restData.payloadObj?.btl ||
    restData.fullPayload?.btl ||
    parsedPayload?.if ||
    restData.if ||
    restData.payloadObj?.if ||
    restData.fullPayload?.if;

  // Debug: Log the structure we're working with
  if (borrowerType === "pension") {
    console.log("🔍 ReviewSubmitStep - Data structure:", {
      hasFormData: !!formData,
      formDataKeys: formData ? Object.keys(formData) : [],
      hasScheme: !!formData?.scheme,
      schemeName: formData?.scheme?.name,
      schemeNumber: formData?.scheme?.number,
      hasMembers: !!formData?.members,
      membersCount: formData?.members?.length || 0,
      hasBtlData: !!btlData,
      btlDataKeys: btlData ? Object.keys(btlData) : [],
      restDataKeys: Object.keys(restData),
    });
  }

  // Extract data based on borrower type
  let personalInfo = null;
  let companyInfo = null;
  let applicantsList = [];
  let creditHistory = null;
  let propertiesOwned = null;
  let uploadsInfo = null;
  let declarations = { agree1: false, agree2: false };

  if (borrowerType === "individual" || borrowerType === "solo") {
    // Solo form structure
    personalInfo = {
      firstName: formData.firstName || btlData?.solo?.firstName || "",
      surname: formData.surname || btlData?.solo?.surname || "",
      email: formData.email || btlData?.solo?.email || "",
      phone: formData.phone || btlData?.solo?.phone || "",
      propertyAddress: formData.propertyAddress || formData.property_address || btlData?.solo?.propertyAddress || "",
    };
    creditHistory = formData.credit || btlData?.solo?.credit || "";
    propertiesOwned = formData.props || btlData?.solo?.props || "";
    // Extract uploads from multiple possible locations
    uploadsInfo = formData.uploads || btlData?.solo_uploads || btlData?.solo?.uploads ||
      restData.if?.solo?.uploads || parsedPayload?.if?.solo?.uploads ||
      restData.uploads || parsedPayload?.btl?.solo_uploads || parsedPayload?.btl?.solo?.uploads || {};
    declarations.agree1 = btlData?.solo_agree1 || restData.if?.solo?.agree1 || false;
    declarations.agree2 = btlData?.solo_agree2 || restData.if?.solo?.agree2 || false;
  } else if (borrowerType === "limited") {
    // Limited form structure
    companyInfo = formData.company || btlData?.limited?.company || {};
    companyInfo.propertyAddress = formData.propertyAddress || formData.property_address || btlData?.limited?.propertyAddress || "";
    applicantsList = formData.applicants || btlData?.limited?.applicants || [];
    // Extract uploads from multiple possible locations
    uploadsInfo = formData.uploads || btlData?.limited?.uploads ||
      restData.if?.limited?.uploads || parsedPayload?.if?.limited?.uploads ||
      restData.uploads || parsedPayload?.btl?.limited?.uploads || {};
    declarations.agree1 = btlData?.limited?.agree1 || false;
    declarations.agree2 = btlData?.limited?.agree2 || false;
  } else if (borrowerType === "partnership") {
    // Partnership form structure - partners are applicants
    const partners = formData.partners || btlData?.partnership?.partners || [];
    applicantsList = partners.map(p => ({
      firstName: p.firstName || "",
      surname: p.surname || "",
      email: p.email || "",
      phone: p.phone || "",
      credit: p.credit || "",
      props: p.props || "",
    }));
    if (applicantsList.length > 0) {
      personalInfo = {
        firstName: applicantsList[0].firstName,
        surname: applicantsList[0].surname,
        email: applicantsList[0].email,
        phone: applicantsList[0].phone,
        propertyAddress: formData.propertyAddress || formData.property_address || btlData?.partnership?.propertyAddress || "",
      };
      creditHistory = applicantsList[0].credit || "";
      propertiesOwned = applicantsList[0].props || "";
    }
    // Extract uploads from multiple possible locations
    uploadsInfo = formData.uploads || btlData?.partnership?.uploads ||
      restData.if?.partnership?.uploads || parsedPayload?.if?.partnership?.uploads ||
      restData.uploads || parsedPayload?.btl?.partnership?.uploads || {};
    declarations.agree1 = btlData?.partnership?.agree1 || false;
    declarations.agree2 = btlData?.partnership?.agree2 || false;
  } else if (borrowerType === "pension") {
    // Pension form structure
    // The PensionForm passes: {type: "pension", scheme, members, uploads }
    // Wizard stores it in formData, and may also structure it as btl.pension

    // Check formData directly first (from Wizard's onNext)
    // Also check restData directly in case data is at top level
    const directScheme = formData.scheme || restData.scheme || (restData.type === "pension" ? restData.scheme : null);
    const directMembers = formData.members || restData.members || (restData.type === "pension" ? restData.members : null);

    // Check multiple possible locations for pension data
    const pension = formData.pension || btlData?.pension || restData.btl?.pension || restData.payloadObj?.btl?.pension || restData.fullPayload?.btl?.pension || {};

    // Extract scheme from multiple possible locations
    // Priority: direct formData.scheme > pension.scheme > btlData.pension.scheme
    const scheme = directScheme || pension.scheme || btlData?.pension?.scheme || restData.btl?.pension?.scheme || restData.payloadObj?.btl?.pension?.scheme || restData.fullPayload?.btl?.pension?.scheme || {};

    // Debug logging
    console.log("🔍 Pension Review - Debug:", {
      hasPension: !!pension,
      hasScheme: !!scheme,
      hasDirectScheme: !!directScheme,
      schemeKeys: Object.keys(scheme),
      schemeName: scheme.name,
      schemeNumber: scheme.number,
      pensionKeys: Object.keys(pension),
      formDataKeys: Object.keys(formData),
      btlDataPension: !!btlData?.pension,
      formDataPension: !!formData.pension,
      restDataKeys: Object.keys(restData),
    });

    // Extract trust/scheme information - check multiple possible locations
    // Priority: direct scheme > pension.scheme > nested structures
    const trustName = scheme.name || directScheme?.name || pension.trustName || pension.scheme?.name || btlData?.pension?.scheme?.name || restData.btl?.pension?.scheme?.name || restData.payloadObj?.btl?.pension?.scheme?.name || restData.fullPayload?.btl?.pension?.scheme?.name || "";
    const trustNumber = scheme.number || directScheme?.number || pension.trustNumber || pension.scheme?.number || btlData?.pension?.scheme?.number || restData.btl?.pension?.scheme?.number || restData.payloadObj?.btl?.pension?.scheme?.number || restData.fullPayload?.btl?.pension?.scheme?.number || "";

    // Extract properties owned from scheme.props
    const propertiesOwnedValue = scheme.props || directScheme?.props || pension.props || btlData?.pension?.scheme?.props || restData.btl?.pension?.scheme?.props || restData.payloadObj?.btl?.pension?.scheme?.props || restData.fullPayload?.btl?.pension?.scheme?.props || "";

    console.log("🔍 Extracted trust info:", { trustName, trustNumber, propertiesOwned: propertiesOwnedValue });

    personalInfo = {
      trustName: trustName,
      trustNumber: trustNumber,
      propertyAddress: formData.propertyAddress || formData.property_address || btlData?.pension?.propertyAddress || pension?.propertyAddress || "",
    };

    // Set properties owned for display
    if (propertiesOwnedValue !== undefined && propertiesOwnedValue !== null && propertiesOwnedValue !== "") {
      propertiesOwned = String(propertiesOwnedValue);
    }

    // Extract members from pension data - check multiple possible locations
    // Priority: direct formData.members > pension.members > nested structures
    const members = directMembers || pension.members || btlData?.pension?.members || formData.members || restData.btl?.pension?.members || restData.payloadObj?.btl?.pension?.members || restData.fullPayload?.btl?.pension?.members || [];
    applicantsList = members.map(m => ({
      firstName: m.firstName || "",
      surname: m.surname || "",
      email: m.email || "",
      phone: m.phone || "",
    }));
    // Extract uploads from multiple possible locations
    uploadsInfo = formData.uploads || pension.uploads || btlData?.pension?.uploads ||
      restData.if?.pension?.uploads || parsedPayload?.if?.pension?.uploads ||
      restData.uploads || parsedPayload?.btl?.pension?.uploads || {};
    declarations.agree1 = btlData?.pension?.agree1 || false;
    declarations.agree2 = btlData?.pension?.agree2 || false;
  }

  // Debug: Log uploads info
  console.log("📎 ReviewSubmitStep - Uploads Info:", {
    hasUploadsInfo: !!uploadsInfo,
    uploadsKeys: uploadsInfo ? Object.keys(uploadsInfo) : [],
    uploadsCount: uploadsInfo ? Object.keys(uploadsInfo).reduce((sum, key) => {
      const arr = uploadsInfo[key];
      return sum + (Array.isArray(arr) ? arr.length : 0);
    }, 0) : 0,
  });

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="rounded-xl bg-sky-50 border border-sky-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center justify-center w-10 h-10 rounded-lg border border-sky-200 bg-white hover:bg-sky-100 text-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Back to application form"
              disabled={isSubmitting || isSaving}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-sky-900">Review your application</h2>
              <p className="text-sm text-sky-800 mt-1">
                Check everything looks correct before submitting.
              </p>
            </div>
          </div>
          {/* Edit button */}
          <button
            className="text-sm font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-2 disabled:opacity-50"
            onClick={() => {
              // Open edit modal instead of navigating
              if (applicationName) {
                // Initialize edit form data from current data
                // Extract from multiple possible locations to ensure we get all data
                let formData = parsedPayload?.formData || restData.formData || restData.payloadObj?.formData || restData.fullPayload?.formData || restData;
                let btlData = parsedPayload?.btl || restData.btl || restData.payloadObj?.btl || restData.fullPayload?.btl;

                // Ensure formData and btlData are objects
                if (!formData || typeof formData !== 'object') formData = {};
                if (!btlData || typeof btlData !== 'object') btlData = {};

                // For pension forms, ensure scheme and members are properly extracted and included
                if (borrowerType === "pension") {
                  // Check multiple locations for scheme and members
                  const scheme = formData?.scheme || btlData?.pension?.scheme || restData.scheme || {};
                  const members = formData?.members || btlData?.pension?.members || restData.members || [];

                  // Ensure btlData.pension exists
                  if (!btlData.pension) btlData.pension = {};

                  // Set scheme and members in both formData and btlData for consistency
                  formData.scheme = scheme;
                  formData.members = members;
                  btlData.pension.scheme = scheme;
                  btlData.pension.members = members;
                }

                // For limited forms, ensure applicants are extracted
                if (borrowerType === "limited") {
                  if (!btlData.limited) btlData.limited = {};
                  if (!formData.applicants && btlData.limited.applicants) {
                    formData.applicants = btlData.limited.applicants;
                  }
                  if (!btlData.limited.applicants && formData.applicants) {
                    btlData.limited.applicants = formData.applicants;
                  }
                }

                // For partnership forms, ensure partners are extracted
                if (borrowerType === "partnership") {
                  if (!btlData.partnership) btlData.partnership = {};
                  if (!formData.partners && btlData.partnership.partners) {
                    formData.partners = btlData.partnership.partners;
                  }
                  if (!btlData.partnership.partners && formData.partners) {
                    btlData.partnership.partners = formData.partners;
                  }
                }

                // Ensure uploads are extracted and included for all borrower types
                // Check multiple possible locations for uploads
                let extractedUploads = formData.uploads || btlData?.solo_uploads || btlData?.solo?.uploads ||
                  btlData?.limited?.uploads || btlData?.partnership?.uploads ||
                  btlData?.pension?.uploads || restData.uploads || {};

                // If uploads found, ensure they're in both formData and btlData
                if (extractedUploads && Object.keys(extractedUploads).length > 0) {
                  formData.uploads = extractedUploads;
                  if (borrowerType === "individual" || borrowerType === "solo") {
                    if (!btlData.solo) btlData.solo = {};
                    btlData.solo.uploads = extractedUploads;
                    btlData.solo_uploads = extractedUploads;
                  } else if (borrowerType === "limited") {
                    if (!btlData.limited) btlData.limited = {};
                    btlData.limited.uploads = extractedUploads;
                  } else if (borrowerType === "partnership") {
                    if (!btlData.partnership) btlData.partnership = {};
                    btlData.partnership.uploads = extractedUploads;
                  } else if (borrowerType === "pension") {
                    if (!btlData.pension) btlData.pension = {};
                    btlData.pension.uploads = extractedUploads;
                  }
                }

                setEditFormData({ formData, btlData, borrowerType });
                setIsEditModalOpen(true);
              } else {
                // If no application name, fall back to navigation
                onEditForm();
              }
            }}
            title="Edit Form"
            disabled={isSubmitting || isSaving}
          >
            Edit form
          </button>
        </div>
      </div>

      {/* Application Summary */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Application Summary</h3>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          <SummaryRow label="Product Type" value={formatProductCategoryLabel(productType)} />
          <SummaryRow label="Product ID" value={productId ? `#${productId}` : "—"} />
          <SummaryRow label="Borrower Type" value={niceBorrower} />
        </dl>
      </div>

      {/* Company Information (Limited only) */}
      {companyInfo && (companyInfo.name || companyInfo.regNo || companyInfo.phone || companyInfo.email) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Company Information</h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {companyInfo.name && <SummaryRow label="Company Name" value={companyInfo.name} />}
            {companyInfo.regNo && <SummaryRow label="Registration Number" value={companyInfo.regNo} />}
            {companyInfo.phone && <SummaryRow label="Phone Number" value={companyInfo.phone} />}
            {companyInfo.email && <SummaryRow label="Email" value={companyInfo.email} />}
            {companyInfo.propertyAddress && <SummaryRow label="Security Address" value={companyInfo.propertyAddress} />}
          </dl>
        </div>
      )}

      {/* Personal Information */}
      {personalInfo && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
            {borrowerType === "pension" ? "Trust Information" : "Personal Information"}
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {/* For pension/trust, show trust info first */}
            {borrowerType === "pension" && personalInfo.trustName && personalInfo.trustName !== "" && (
              <SummaryRow label="Trust Name" value={personalInfo.trustName} />
            )}
            {borrowerType === "pension" && personalInfo.trustNumber && personalInfo.trustNumber !== "" && (
              <SummaryRow label="Trust Number" value={personalInfo.trustNumber} />
            )}
            {/* For other borrower types, show personal info */}
            {borrowerType !== "pension" && personalInfo.firstName && (
              <SummaryRow label="First Name" value={personalInfo.firstName} />
            )}
            {borrowerType !== "pension" && personalInfo.surname && (
              <SummaryRow label="Surname" value={personalInfo.surname} />
            )}
            {borrowerType !== "pension" && personalInfo.email && (
              <SummaryRow label="Email" value={personalInfo.email} />
            )}
            {borrowerType !== "pension" && personalInfo.phone && (
              <SummaryRow label="Phone Number" value={personalInfo.phone} />
            )}
            {personalInfo.propertyAddress && (
              <SummaryRow label="Security Address" value={personalInfo.propertyAddress} />
            )}
            {creditHistory && <SummaryRow label="Credit History" value={creditHistory} />}
            {propertiesOwned !== undefined && propertiesOwned !== "" && (
              <SummaryRow label="Properties Owned" value={String(propertiesOwned)} />
            )}
          </dl>
        </div>
      )}

      {/* Applicants/Partners/Members List */}
      {applicantsList.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
            {borrowerType === "partnership" ? "Partners" : borrowerType === "pension" ? "Members" : "Applicants"}
          </h3>
          <div className="space-y-4">
            {applicantsList.map((applicant, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">
                  {borrowerType === "partnership" ? `Partner ${idx + 1}` : borrowerType === "pension" ? `Member ${idx + 1}` : `Applicant ${idx + 1}`}
                </h4>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {applicant.firstName && <SummaryRow label="First Name" value={applicant.firstName} />}
                  {applicant.surname && <SummaryRow label="Surname" value={applicant.surname} />}
                  {applicant.email && <SummaryRow label="Email" value={applicant.email} />}
                  {applicant.phone && <SummaryRow label="Phone" value={applicant.phone} />}
                  {applicant.credit && <SummaryRow label="Credit History" value={applicant.credit} />}
                  {applicant.props !== undefined && applicant.props !== "" && (
                    <SummaryRow label="Properties Owned" value={String(applicant.props)} />
                  )}
                </dl>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents/Uploads */}
      {uploadsInfo && Object.keys(uploadsInfo).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Check for both old keys (propertyAccounts) and new keys (borrower_accounts) */}
            {(() => {
              const files = uploadsInfo.propertyAccounts || uploadsInfo.borrower_accounts || [];
              return Array.isArray(files) && files.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Property Accounts</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {(() => {
              const files = uploadsInfo.bankStatement || uploadsInfo.borrower_bank_statements || [];
              return Array.isArray(files) && files.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">3 months bank statements</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {(() => {
              const files = uploadsInfo.propertyPortfolio || uploadsInfo.borrower_portfolio || [];
              return Array.isArray(files) && files.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Property Portfolio</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {(() => {
              const files = uploadsInfo.assetsLiability || uploadsInfo.app_assets_liability || [];
              return Array.isArray(files) && files.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Assets & Liability Statement</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {(() => {
              const files = uploadsInfo.identification || uploadsInfo.app_id || [];
              return Array.isArray(files) && files.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Identification</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {(() => {
              const files = uploadsInfo.personalBankStatements || uploadsInfo.app_personal_bank || [];
              return Array.isArray(files) && files.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">3 months Personal Bank statements</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {uploadsInfo.app_self_employed &&
              Array.isArray(uploadsInfo.app_self_employed) &&
              uploadsInfo.app_self_employed.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Self Employed Documents</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {uploadsInfo.app_self_employed.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            {uploadsInfo.app_employed &&
              Array.isArray(uploadsInfo.app_employed) &&
              uploadsInfo.app_employed.length > 0 && (
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Employed Documents</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {uploadsInfo.app_employed.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === 'string' ? file : 'File')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            {IF_UPLOAD_BUCKETS.map((bucket) => {
              const legacyKey = Object.keys(IF_UPLOAD_FIELD_ALIASES).find(
                (k) => IF_UPLOAD_FIELD_ALIASES[k] === bucket.key && k !== bucket.key
              );
              const files =
                uploadsInfo[bucket.key] ||
                (legacyKey ? uploadsInfo[legacyKey] : null) ||
                [];
              if (!Array.isArray(files) || files.length === 0) return null;
              return (
                <div key={bucket.key} className="border border-slate-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">{bucket.title}</div>
                  <div className="text-xs text-slate-600 space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="truncate" title={file.file_name || file.name || file}>
                        • {file.file_name || file.name || (typeof file === "string" ? file : "File")}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Declarations - Checkboxes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">Declarations</h3>

        {/* First Declaration Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={declaration1}
            onChange={(e) => setDeclaration1(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 focus:ring-2"
            disabled={isSubmitting || isSaving}
          />
          <span className="text-sm text-slate-900 leading-relaxed">
            I declare that the information provided in this application is true and complete to the best of my knowledge. I understand that providing false information may invalidate my application.
          </span>
        </label>

        {/* Second Declaration Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={declaration2}
            onChange={(e) => setDeclaration2(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 focus:ring-2"
            disabled={isSubmitting || isSaving}
          />
          <span className="text-sm text-slate-900 leading-relaxed">
            Your property may be repossessed if you do not keep up repayments on your mortgage. Not all Buy to Let Mortgages are regulated by The Financial Conduct Authority.
          </span>
        </label>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-3">
        <button
          className="rounded-xl px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-50"
          onClick={onBack}
          disabled={isSubmitting || isSaving}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          className="rounded-xl px-5 py-2.5 text-sm md:text-base font-semibold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // CRITICAL: Only submit if both declarations are checked
            // This prevents accidental submissions
            if (!declaration1 || !declaration2) {
              toast.error("Please accept both declarations to submit the application.");
              return;
            }
            // Explicitly call onSubmit with declarations
            console.log("✅ Submit button clicked - initiating submission", { agree1: declaration1, agree2: declaration2 });
            onSubmit({ agree1: declaration1, agree2: declaration2 });
          }}
          disabled={isSubmitting || isSaving || !declaration1 || !declaration2}
          title={!declaration1 || !declaration2 ? "Please accept both declarations to submit" : ""}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : "Submit Application"}
        </button>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && editFormData && applicationName && (
        <EditFormModal
          borrowerType={borrowerType}
          formData={editFormData.formData}
          btlData={editFormData.btlData}
          applicationName={applicationName}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditFormData(null);
          }}
          onSave={async (updatedData) => {
            setIsSaving(true);
            try {
              // ✅ ONLY UPDATE LOCAL STATE - no backend call
              // Backend will be updated on final submit

              const updatedLocalData = {
                ...currentData,
                formData: updatedData.formData,
                payloadObj: {
                  ...currentData?.payloadObj,
                  formData: updatedData.formData,
                },
                fullPayload: {
                  ...currentData?.fullPayload,
                  formData: updatedData.formData,
                  btl: updatedData.btlData,
                },
                btl: updatedData.btlData,
              };

              setLocalData(updatedLocalData);

              // Update parent component state
              if (onDataUpdate) {
                onDataUpdate({
                  formData: updatedData.formData,
                  btlData: updatedData.btlData,
                });
              }

              toast.success("Changes saved locally");
              setIsEditModalOpen(false);
              setEditFormData(null);
            } catch (error) {
              console.error("Failed to update data:", error);
              toast.error("Failed to save changes. Please try again.");
            } finally {
              setIsSaving(false);
            }
          }}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-600">{label}</dt>
      <dd className="font-semibold text-slate-900 text-right">{value}</dd>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function labelize(key) {
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

// eslint-disable-next-line no-unused-vars
function formatValue(v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return "—"; }
  }
  return String(v);
}

/* ----------------------- Edit Form Modal ----------------------- */
function EditFormModal({ borrowerType, formData, btlData, applicationName, onClose, onSave, isSaving }) {
  // Extract uploads based on borrower type
  const getInitialUploads = () => {
    // Extract uploads from multiple possible locations
    let extractedUploads = {};

    if (borrowerType === "individual" || borrowerType === "solo") {
      extractedUploads = formData?.uploads || btlData?.solo_uploads || btlData?.solo?.uploads || {};
    } else if (borrowerType === "limited") {
      extractedUploads = formData?.uploads || btlData?.limited?.uploads || {};
    } else if (borrowerType === "partnership") {
      extractedUploads = formData?.uploads || btlData?.partnership?.uploads || {};
    } else if (borrowerType === "pension") {
      extractedUploads = formData?.uploads || btlData?.pension?.uploads || {};
    }

    // Map old keys to new keys if needed (for backward compatibility)
    // Forms use: borrower_accounts, borrower_bank_statements, borrower_portfolio, app_assets_liability, etc.
    // Edit modal might have: propertyAccounts, bankStatement, etc.
    const mappedUploads = { ...extractedUploads };

    // Map old keys to new keys if they exist
    if (mappedUploads.propertyAccounts && !mappedUploads.borrower_accounts) {
      mappedUploads.borrower_accounts = mappedUploads.propertyAccounts;
    }
    if (mappedUploads.bankStatement && !mappedUploads.borrower_bank_statements) {
      mappedUploads.borrower_bank_statements = mappedUploads.bankStatement;
    }
    if (mappedUploads.propertyPortfolio && !mappedUploads.borrower_portfolio) {
      mappedUploads.borrower_portfolio = mappedUploads.propertyPortfolio;
    }
    if (mappedUploads.assetsLiability && !mappedUploads.app_assets_liability) {
      mappedUploads.app_assets_liability = mappedUploads.assetsLiability;
    }
    if (mappedUploads.identification && !mappedUploads.app_id) {
      mappedUploads.app_id = mappedUploads.identification;
    }
    if (mappedUploads.personalBankStatements && !mappedUploads.app_personal_bank) {
      mappedUploads.app_personal_bank = mappedUploads.personalBankStatements;
    }

    console.log("📎 EditFormModal - Initial Uploads:", {
      borrowerType,
      hasUploads: !!mappedUploads,
      uploadsKeys: Object.keys(mappedUploads),
      uploadsCount: Object.keys(mappedUploads).reduce((sum, key) => {
        const arr = mappedUploads[key];
        return sum + (Array.isArray(arr) ? arr.length : 0);
      }, 0),
      sampleUploads: Object.keys(mappedUploads).reduce((acc, key) => {
        const arr = mappedUploads[key];
        if (Array.isArray(arr) && arr.length > 0) {
          acc[key] = arr.slice(0, 2).map(f => ({ name: f.file_name || f.name, url: f.file_url || f.url }));
        }
        return acc;
      }, {}),
    });

    return mappedUploads;
  };

  const [uploads, setUploads] = useState(getInitialUploads);
  // eslint-disable-next-line no-unused-vars
  const [uploading, setUploading] = useState(false);
  // Track uploading files with progress: {[bucketKey]: [{id, name, progress}] }
  const [uploadingFiles, setUploadingFiles] = useState({});

  // Extract and initialize form data based on borrower type
  const [localFormData, setLocalFormData] = useState(() => {
    if (borrowerType === "individual" || borrowerType === "solo") {
      return {
        firstName: formData?.firstName || btlData?.solo?.firstName || "",
        surname: formData?.surname || btlData?.solo?.surname || "",
        email: formData?.email || btlData?.solo?.email || "",
        phone: formData?.phone || btlData?.solo?.phone || "",
        credit: formData?.credit || btlData?.solo?.credit || "",
        props: formData?.props || btlData?.solo?.props || "",
        agree1: btlData?.solo_agree1 || btlData?.solo?.agree1 || false,
        agree2: btlData?.solo_agree2 || btlData?.solo?.agree2 || false,
      };
    } else if (borrowerType === "limited") {
      return {
        company: formData?.company || btlData?.limited?.company || { name: "", regNo: "", phone: "", email: "" },
        applicants: formData?.applicants || btlData?.limited?.applicants || [],
        agree1: btlData?.limited?.agree1 || false,
        agree2: btlData?.limited?.agree2 || false,
      };
    } else if (borrowerType === "partnership") {
      return {
        partners: formData?.partners || btlData?.partnership?.partners || [],
        agree1: btlData?.partnership?.agree1 || false,
        agree2: btlData?.partnership?.agree2 || false,
      };
    } else if (borrowerType === "pension") {
      // Extract scheme and members from pension structure
      const scheme = formData?.scheme || btlData?.pension?.scheme || {};
      const members = formData?.members || btlData?.pension?.members || [];
      return {
        scheme: {
          name: scheme.name || scheme.trustName || "",
          number: scheme.number || scheme.trustNumber || "",
          props: scheme.props || "",
          credit: scheme.credit || "",
        },
        members: members.length > 0 ? members : [{ id: Date.now().toString(), firstName: "", surname: "", phone: "", email: "" }],
        agree1: btlData?.pension?.agree1 || false,
        agree2: btlData?.pension?.agree2 || false,
      };
    }
    return {};
  });

  const digitsOnly = (value) => (value || "").replace(/\D/g, "");
  const phonesInvalid = !arePhonesValid(applicationFormPhones(borrowerType, localFormData));

  // Handle file upload
  const handleUpload = (bucketKey, fieldname) => async (files) => {
    if (!applicationName) {
      toast.error("Application name is required for file uploads");
      return;
    }
    if (!files?.length) return;

    setUploading(true);
    const nextBucket = [...(uploads[bucketKey] || [])];

    for (const file of files) {
      const uploadId = Date.now().toString() + Math.random().toString(16).slice(2);
      const fileName = file.name;

      // Add to uploadingFiles state to show progress bar
      setUploadingFiles(prev => ({
        ...prev,
        [bucketKey]: [...(prev[bucketKey] || []), { id: uploadId, name: fileName, progress: 0 }]
      }));

      try {
        const up = await uploadFileToApplication({
          file,
          applicationName,
          fieldname,
          isPrivate: true,
          onProgress: (percent) => {
            // Update progress for this specific file
            setUploadingFiles(prev => ({
              ...prev,
              [bucketKey]: (prev[bucketKey] || []).map(u =>
                u.id === uploadId ? { ...u, progress: percent } : u
              )
            }));
          },
        });
        nextBucket.push({
          id: uploadId,
          file_url: up?.file_url,
          file_name: up?.file_name,
          name: up?.name
        });
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Failed to upload ${fileName}`);
      } finally {
        // Remove from uploadingFiles state when complete or on error
        setUploadingFiles(prev => ({
          ...prev,
          [bucketKey]: (prev[bucketKey] || []).filter(u => u.id !== uploadId)
        }));
      }
    }

    const nextUploads = { ...uploads, [bucketKey]: nextBucket };
    setUploads(nextUploads);
    setUploading(false);
    if (nextBucket.length > 0) {
      toast.success("File(s) uploaded successfully");
    }
  };

  // Handle file removal
  const handleRemove = (bucketKey) => async (fileId) => {
    const nextBucket = (uploads[bucketKey] || []).filter(f => (f.id !== fileId && f.name !== fileId));
    const nextUploads = { ...uploads, [bucketKey]: nextBucket };
    setUploads(nextUploads);
    toast.success("File removed");
  };

  const handleSave = () => {
    // Reconstruct the btlData structure based on borrower type
    let updatedBtlData = { ...btlData };
    let updatedFormData = { ...formData };

    if (borrowerType === "individual" || borrowerType === "solo") {
      updatedBtlData = {
        ...updatedBtlData,
        solo: {
          firstName: localFormData.firstName,
          surname: localFormData.surname,
          email: localFormData.email,
          phone: localFormData.phone,
          credit: localFormData.credit,
          props: localFormData.props,
          uploads: uploads,
        },
        solo_uploads: uploads, // Also store in solo_uploads for compatibility
        solo_agree1: localFormData.agree1,
        solo_agree2: localFormData.agree2,
      };
      updatedFormData = {
        ...updatedFormData,
        firstName: localFormData.firstName,
        surname: localFormData.surname,
        email: localFormData.email,
        phone: localFormData.phone,
        credit: localFormData.credit,
        props: localFormData.props,
        uploads: uploads,
      };
    } else if (borrowerType === "limited") {
      updatedBtlData = {
        ...updatedBtlData,
        limited: {
          company: localFormData.company,
          applicants: localFormData.applicants,
          uploads: uploads,
          agree1: localFormData.agree1,
          agree2: localFormData.agree2,
        },
      };
      updatedFormData = {
        ...updatedFormData,
        company: localFormData.company,
        applicants: localFormData.applicants,
        uploads: uploads,
      };
    } else if (borrowerType === "partnership") {
      updatedBtlData = {
        ...updatedBtlData,
        partnership: {
          partners: localFormData.partners,
          uploads: uploads,
          agree1: localFormData.agree1,
          agree2: localFormData.agree2,
        },
      };
      updatedFormData = {
        ...updatedFormData,
        partners: localFormData.partners,
        uploads: uploads,
      };
    } else if (borrowerType === "pension") {
      updatedBtlData = {
        ...updatedBtlData,
        pension: {
          scheme: localFormData.scheme,
          members: localFormData.members,
          uploads: uploads,
          agree1: localFormData.agree1,
          agree2: localFormData.agree2,
        },
      };
      updatedFormData = {
        ...updatedFormData,
        scheme: localFormData.scheme,
        members: localFormData.members,
        uploads: uploads,
      };
    }

    onSave({
      formData: updatedFormData,
      btlData: updatedBtlData,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Edit Application Form</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            disabled={isSaving}
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Solo/Individual Form */}
          {(borrowerType === "individual" || borrowerType === "solo") && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={localFormData.firstName}
                    onChange={(e) => setLocalFormData({ ...localFormData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Surname *</label>
                  <input
                    type="text"
                    value={localFormData.surname}
                    onChange={(e) => setLocalFormData({ ...localFormData, surname: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                    placeholder="Enter surname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={localFormData.email}
                    onChange={(e) => setLocalFormData({ ...localFormData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                  <PhoneField
                    value={localFormData.phone}
                    onChange={(next) => setLocalFormData({ ...localFormData, phone: next })}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Number of properties owned *</label>
                  <input
                    type="number"
                    min="0"
                    value={localFormData.props}
                    onChange={(e) => setLocalFormData({ ...localFormData, props: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                    placeholder="5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Security Address *</label>
                  <input
                    type="text"
                    value={localFormData.propertyAddress || ""}
                    onChange={(e) => setLocalFormData({ ...localFormData, propertyAddress: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                    placeholder="Enter security address"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Credit History *</label>
                  <div className="flex gap-3">
                    {["Good", "Bad", "Poor"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setLocalFormData({ ...localFormData, credit: opt })}
                        className={`px-4 py-2 rounded-full border text-xs font-semibold transition-all flex-1 ${localFormData.credit === opt
                          ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                          }`}
                        disabled={isSaving}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Uploads Section for Solo */}
              {applicationName && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4">Documents</h4>
                  <div className="space-y-3">
                    <SleekUploadTile
                      title="Property Accounts (if available)"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.borrower_accounts || uploads.propertyAccounts || []}
                      uploadingFiles={uploadingFiles.borrower_accounts || uploadingFiles.propertyAccounts || []}
                      onFiles={handleUpload("borrower_accounts", "borrower_accounts")}
                      onRemove={handleRemove("borrower_accounts")}
                    />
                    <SleekUploadTile
                      title="3 months bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.borrower_bank_statements || uploads.bankStatement || []}
                      uploadingFiles={uploadingFiles.borrower_bank_statements || uploadingFiles.bankStatement || []}
                      onFiles={handleUpload("borrower_bank_statements", "borrower_bank_statements")}
                      onRemove={handleRemove("borrower_bank_statements")}
                    />
                    <SleekUploadTile
                      title="Property Portfolio"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.borrower_portfolio || uploads.propertyPortfolio || []}
                      uploadingFiles={uploadingFiles.borrower_portfolio || uploadingFiles.propertyPortfolio || []}
                      onFiles={handleUpload("borrower_portfolio", "borrower_portfolio")}
                      onRemove={handleRemove("borrower_portfolio")}
                    />
                    <SleekUploadTile
                      title="Assets & Liability Statement"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.app_assets_liability || uploads.assetsLiability || []}
                      uploadingFiles={uploadingFiles.app_assets_liability || uploadingFiles.assetsLiability || []}
                      onFiles={handleUpload("app_assets_liability", "app_assets_liability")}
                      onRemove={handleRemove("app_assets_liability")}
                    />
                    <SleekUploadTile
                      title="Self Employed"
                      sub="Last 2 years Tax Returns (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_self_employed || []}
                      uploadingFiles={uploadingFiles.app_self_employed || []}
                      onFiles={handleUpload("app_self_employed", "app_self_employed")}
                      onRemove={handleRemove("app_self_employed")}
                    />
                    <SleekUploadTile
                      title="Employed"
                      sub="P60 and 3 months Payslips (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_employed || []}
                      uploadingFiles={uploadingFiles.app_employed || []}
                      onFiles={handleUpload("app_employed", "app_employed")}
                      onRemove={handleRemove("app_employed")}
                    />
                    <SleekUploadTile
                      title="3 months Personal Bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.app_personal_bank || uploads.personalBankStatements || []}
                      uploadingFiles={uploadingFiles.app_personal_bank || uploadingFiles.personalBankStatements || []}
                      onFiles={handleUpload("app_personal_bank", "app_personal_bank")}
                      onRemove={handleRemove("app_personal_bank")}
                    />
                    <SleekUploadTile
                      title="Identification"
                      sub="Passport and/or Driver's Licence (PDF, JPG, or PNG up to 10MB each)"
                      existingFiles={uploads.app_id || uploads.identification || []}
                      uploadingFiles={uploadingFiles.app_id || uploadingFiles.identification || []}
                      onFiles={handleUpload("app_id", "app_id")}
                      onRemove={handleRemove("app_id")}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Limited Company Form */}
          {borrowerType === "limited" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={localFormData.company?.name || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      company: { ...localFormData.company, name: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Registration Number *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={localFormData.company?.regNo || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      company: { ...localFormData.company, regNo: digitsOnly(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company Phone *</label>
                  <PhoneField
                    value={localFormData.company?.phone || ""}
                    mobileOnly={false}
                    onChange={(next) => setLocalFormData({
                      ...localFormData,
                      company: { ...localFormData.company, phone: next }
                    })}
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company Email *</label>
                  <input
                    type="email"
                    value={localFormData.company?.email || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      company: { ...localFormData.company, email: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Number of properties owned *</label>
                  <input
                    type="number"
                    min="0"
                    value={localFormData.props || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      props: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Security Address *</label>
                  <input
                    type="text"
                    value={localFormData.propertyAddress || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      propertyAddress: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                    placeholder="Enter security address"
                  />
                </div>
              </div>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700 mb-2">Applicants/Directors *</label>
                {localFormData.applicants && localFormData.applicants.length > 0 ? (
                  localFormData.applicants.map((applicant, idx) => (
                    <div key={applicant.id || idx} className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-sm">Applicant {idx + 1}</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                          <input
                            type="text"
                            value={applicant.firstName || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], firstName: e.target.value };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Surname</label>
                          <input
                            type="text"
                            value={applicant.surname || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], surname: e.target.value };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={applicant.email || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], email: e.target.value };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                          <PhoneField
                            value={applicant.phone || ""}
                            required={idx === 0}
                            onChange={(next) => {
                              const updated = [...localFormData.applicants];
                              updated[idx] = { ...updated[idx], phone: next };
                              setLocalFormData({ ...localFormData, applicants: updated });
                            }}
                            disabled={isSaving}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No applicants found</p>
                )}
              </div>

              {/* Uploads Section for Limited */}
              {applicationName && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4">Documents</h4>
                  <div className="space-y-3">
                    <SleekUploadTile
                      title="Property Accounts (if available)"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.borrower_accounts || uploads.propertyAccounts || []}
                      uploadingFiles={uploadingFiles.borrower_accounts || uploadingFiles.propertyAccounts || []}
                      onFiles={handleUpload("borrower_accounts", "borrower_accounts")}
                      onRemove={handleRemove("borrower_accounts")}
                    />
                    <SleekUploadTile
                      title="3 months bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.borrower_bank_statements || uploads.bankStatement || []}
                      uploadingFiles={uploadingFiles.borrower_bank_statements || uploadingFiles.bankStatement || []}
                      onFiles={handleUpload("borrower_bank_statements", "borrower_bank_statements")}
                      onRemove={handleRemove("borrower_bank_statements")}
                    />
                    <SleekUploadTile
                      title="Property Portfolio"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.borrower_portfolio || uploads.propertyPortfolio || []}
                      uploadingFiles={uploadingFiles.borrower_portfolio || uploadingFiles.propertyPortfolio || []}
                      onFiles={handleUpload("borrower_portfolio", "borrower_portfolio")}
                      onRemove={handleRemove("borrower_portfolio")}
                    />
                    <SleekUploadTile
                      title="Assets & Liability Statement"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.app_assets_liability || uploads.assetsLiability || []}
                      uploadingFiles={uploadingFiles.app_assets_liability || uploadingFiles.assetsLiability || []}
                      onFiles={handleUpload("app_assets_liability", "app_assets_liability")}
                      onRemove={handleRemove("app_assets_liability")}
                    />
                    <SleekUploadTile
                      title="Self Employed"
                      sub="Last 2 years Tax Returns (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_self_employed || []}
                      uploadingFiles={uploadingFiles.app_self_employed || []}
                      onFiles={handleUpload("app_self_employed", "app_self_employed")}
                      onRemove={handleRemove("app_self_employed")}
                    />
                    <SleekUploadTile
                      title="Employed"
                      sub="P60 and 3 months Payslips (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_employed || []}
                      uploadingFiles={uploadingFiles.app_employed || []}
                      onFiles={handleUpload("app_employed", "app_employed")}
                      onRemove={handleRemove("app_employed")}
                    />
                    <SleekUploadTile
                      title="3 months Personal Bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.app_personal_bank || uploads.personalBankStatements || []}
                      uploadingFiles={uploadingFiles.app_personal_bank || uploadingFiles.personalBankStatements || []}
                      onFiles={handleUpload("app_personal_bank", "app_personal_bank")}
                      onRemove={handleRemove("app_personal_bank")}
                    />
                    <SleekUploadTile
                      title="Identification"
                      sub="Passport and/or Driver's Licence (PDF, JPG, or PNG up to 10MB each)"
                      existingFiles={uploads.app_id || uploads.identification || []}
                      uploadingFiles={uploadingFiles.app_id || uploadingFiles.identification || []}
                      onFiles={handleUpload("app_id", "app_id")}
                      onRemove={handleRemove("app_id")}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Partnership Form */}
          {borrowerType === "partnership" && (
            <>
              {localFormData.partners && localFormData.partners.length > 0 ? (
                <div className="space-y-6">
                  <h4 className="font-bold text-[16px] text-slate-900 border-b pb-2">Personal Information</h4>
                  {localFormData.partners.map((partner, idx) => (
                    <div key={partner.id || idx} className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-sm text-slate-700">
                        {idx === 0 ? "Main Partner" : `Partner ${idx + 1}`}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                          <input
                            type="text"
                            value={partner.firstName || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.partners];
                              updated[idx] = { ...updated[idx], firstName: e.target.value };
                              setLocalFormData({ ...localFormData, partners: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Surname</label>
                          <input
                            type="text"
                            value={partner.surname || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.partners];
                              updated[idx] = { ...updated[idx], surname: e.target.value };
                              setLocalFormData({ ...localFormData, partners: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={partner.email || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.partners];
                              updated[idx] = { ...updated[idx], email: e.target.value };
                              setLocalFormData({ ...localFormData, partners: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                          <PhoneField
                            value={partner.phone || ""}
                            required={idx === 0}
                            onChange={(next) => {
                              const updated = [...localFormData.partners];
                              updated[idx] = { ...updated[idx], phone: next };
                              setLocalFormData({ ...localFormData, partners: updated });
                            }}
                            disabled={isSaving}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-2">Credit History *</label>
                          <div className="flex gap-3">
                            {["Good", "Bad", "Poor"].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => {
                                  const updated = [...localFormData.partners];
                                  updated[idx] = { ...updated[idx], credit: opt };
                                  setLocalFormData({ ...localFormData, partners: updated });
                                }}
                                className={`px-4 py-2 rounded-full border text-xs font-semibold transition-all flex-1 ${partner.credit === opt
                                  ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                                  }`}
                                disabled={isSaving}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Security Address</label>
                          <input
                            type="text"
                            value={partner.propertyAddress || localFormData.propertyAddress || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.partners];
                              updated[idx] = { ...updated[idx], propertyAddress: e.target.value };
                              setLocalFormData({ ...localFormData, partners: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Properties Owned</label>
                          <input
                            type="number"
                            min="0"
                            value={partner.props || localFormData.props || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.partners];
                              updated[idx] = { ...updated[idx], props: e.target.value };
                              setLocalFormData({ ...localFormData, partners: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="text-center pt-2">
                    <p className="text-sm font-bold text-slate-800 mb-2">Please add partners as applicants</p>
                    {/* Note: Addition of partners in edit modal is typically handled elsewhere or restricted in simple edit views */}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No partners found. Please add partners as applicants.</p>
              )}


              {/* Uploads Section for Partnership */}
              {applicationName && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4">Documents</h4>
                  <div className="space-y-3">
                    <SleekUploadTile
                      title="Property Accounts (if available)"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.borrower_accounts || uploads.propertyAccounts || []}
                      uploadingFiles={uploadingFiles.borrower_accounts || uploadingFiles.propertyAccounts || []}
                      onFiles={handleUpload("borrower_accounts", "borrower_accounts")}
                      onRemove={handleRemove("borrower_accounts")}
                    />
                    <SleekUploadTile
                      title="3 months bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.borrower_bank_statements || uploads.bankStatement || []}
                      uploadingFiles={uploadingFiles.borrower_bank_statements || uploadingFiles.bankStatement || []}
                      onFiles={handleUpload("borrower_bank_statements", "borrower_bank_statements")}
                      onRemove={handleRemove("borrower_bank_statements")}
                    />
                    <SleekUploadTile
                      title="Property Portfolio"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.borrower_portfolio || uploads.propertyPortfolio || []}
                      uploadingFiles={uploadingFiles.borrower_portfolio || uploadingFiles.propertyPortfolio || []}
                      onFiles={handleUpload("borrower_portfolio", "borrower_portfolio")}
                      onRemove={handleRemove("borrower_portfolio")}
                    />
                    <SleekUploadTile
                      title="Assets & Liability Statement"
                      sub="PDF, JPG, or PNG up to 10MB"
                      existingFiles={uploads.app_assets_liability || uploads.assetsLiability || []}
                      uploadingFiles={uploadingFiles.app_assets_liability || uploadingFiles.assetsLiability || []}
                      onFiles={handleUpload("app_assets_liability", "app_assets_liability")}
                      onRemove={handleRemove("app_assets_liability")}
                    />
                    <SleekUploadTile
                      title="Self Employed"
                      sub="Last 2 years Tax Returns (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_self_employed || []}
                      uploadingFiles={uploadingFiles.app_self_employed || []}
                      onFiles={handleUpload("app_self_employed", "app_self_employed")}
                      onRemove={handleRemove("app_self_employed")}
                    />
                    <SleekUploadTile
                      title="Employed"
                      sub="P60 and 3 months Payslips (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_employed || []}
                      uploadingFiles={uploadingFiles.app_employed || []}
                      onFiles={handleUpload("app_employed", "app_employed")}
                      onRemove={handleRemove("app_employed")}
                    />
                    <SleekUploadTile
                      title="3 months Personal Bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.app_personal_bank || uploads.personalBankStatements || []}
                      uploadingFiles={uploadingFiles.app_personal_bank || uploadingFiles.personalBankStatements || []}
                      onFiles={handleUpload("app_personal_bank", "app_personal_bank")}
                      onRemove={handleRemove("app_personal_bank")}
                    />
                    <SleekUploadTile
                      title="Identification"
                      sub="Passport and/or Driver's Licence (PDF, JPG, or PNG up to 10MB each)"
                      existingFiles={uploads.app_id || uploads.identification || []}
                      uploadingFiles={uploadingFiles.app_id || uploadingFiles.identification || []}
                      onFiles={handleUpload("app_id", "app_id")}
                      onRemove={handleRemove("app_id")}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Pension/Trust Form */}
          {borrowerType === "pension" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trust Name *</label>
                  <input
                    type="text"
                    value={localFormData.scheme?.name || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      scheme: { ...localFormData.scheme, name: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trust Number</label>
                  <input
                    type="text"
                    value={localFormData.scheme?.number || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      scheme: { ...localFormData.scheme, number: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Number of properties owned *</label>
                  <input
                    type="number"
                    min="0"
                    value={localFormData.scheme?.props || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      scheme: { ...localFormData.scheme, props: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Security Address *</label>
                  <input
                    type="text"
                    value={localFormData.propertyAddress || ""}
                    onChange={(e) => setLocalFormData({
                      ...localFormData,
                      propertyAddress: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    disabled={isSaving}
                    placeholder="Enter security address"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Credit History *</label>
                  <div className="flex gap-3">
                    {["Good", "Bad", "Poor"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setLocalFormData({
                          ...localFormData,
                          scheme: { ...localFormData.scheme, credit: opt }
                        })}
                        className={`px-4 py-2 rounded-full border text-xs font-semibold transition-all flex-1 ${localFormData.scheme?.credit === opt
                          ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                          }`}
                        disabled={isSaving}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700 mb-2">Members *</label>
                {localFormData.members && localFormData.members.length > 0 ? (
                  localFormData.members.map((member, idx) => (
                    <div key={member.id || idx} className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-sm">Member {idx + 1}</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                          <input
                            type="text"
                            value={member.firstName || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], firstName: e.target.value };
                              setLocalFormData({ ...localFormData, members: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Surname</label>
                          <input
                            type="text"
                            value={member.surname || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], surname: e.target.value };
                              setLocalFormData({ ...localFormData, members: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={member.email || ""}
                            onChange={(e) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], email: e.target.value };
                              setLocalFormData({ ...localFormData, members: updated });
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            disabled={isSaving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                          <PhoneField
                            value={member.phone || ""}
                            required={idx === 0}
                            onChange={(next) => {
                              const updated = [...localFormData.members];
                              updated[idx] = { ...updated[idx], phone: next };
                              setLocalFormData({ ...localFormData, members: updated });
                            }}
                            disabled={isSaving}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No members found</p>
                )}
              </div>

              {/* Uploads Section for Pension */}
              {applicationName && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4">Documents</h4>
                  <div className="space-y-3">
                    <SleekUploadTile
                      title="Property Accounts (if available)"
                      sub="PDF, JPG, or PNG up to 5MB"
                      existingFiles={uploads.borrower_accounts || uploads.propertyAccounts || []}
                      uploadingFiles={uploadingFiles.borrower_accounts || uploadingFiles.propertyAccounts || []}
                      onFiles={handleUpload("borrower_accounts", "borrower_accounts")}
                      onRemove={handleRemove("borrower_accounts")}
                    />
                    <SleekUploadTile
                      title="3 months bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.borrower_bank_statements || uploads.bankStatement || []}
                      uploadingFiles={uploadingFiles.borrower_bank_statements || uploadingFiles.bankStatement || []}
                      onFiles={handleUpload("borrower_bank_statements", "borrower_bank_statements")}
                      onRemove={handleRemove("borrower_bank_statements")}
                    />
                    <SleekUploadTile
                      title="Property Portfolio"
                      sub="PDF, JPG, or PNG up to 5MB"
                      existingFiles={uploads.borrower_portfolio || uploads.propertyPortfolio || []}
                      uploadingFiles={uploadingFiles.borrower_portfolio || uploadingFiles.propertyPortfolio || []}
                      onFiles={handleUpload("borrower_portfolio", "borrower_portfolio")}
                      onRemove={handleRemove("borrower_portfolio")}
                    />
                    <SleekUploadTile
                      title="Assets & Liability Statement"
                      sub="PDF, JPG, or PNG up to 5MB"
                      existingFiles={uploads.app_assets_liability || uploads.assetsLiability || []}
                      uploadingFiles={uploadingFiles.app_assets_liability || uploadingFiles.assetsLiability || []}
                      onFiles={handleUpload("app_assets_liability", "app_assets_liability")}
                      onRemove={handleRemove("app_assets_liability")}
                    />
                    <SleekUploadTile
                      title="Self Employed"
                      sub="Last 2 years Tax Returns (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_self_employed || []}
                      uploadingFiles={uploadingFiles.app_self_employed || []}
                      onFiles={handleUpload("app_self_employed", "app_self_employed")}
                      onRemove={handleRemove("app_self_employed")}
                    />
                    <SleekUploadTile
                      title="Employed"
                      sub="P60 and 3 months Payslips (PDF, JPG, or PNG up to 10MB)"
                      existingFiles={uploads.app_employed || []}
                      uploadingFiles={uploadingFiles.app_employed || []}
                      onFiles={handleUpload("app_employed", "app_employed")}
                      onRemove={handleRemove("app_employed")}
                    />
                    <SleekUploadTile
                      title="3 months Personal Bank statements"
                      sub="PDF, JPG, or PNG up to 10MB total"
                      existingFiles={uploads.app_personal_bank || uploads.personalBankStatements || []}
                      uploadingFiles={uploadingFiles.app_personal_bank || uploadingFiles.personalBankStatements || []}
                      onFiles={handleUpload("app_personal_bank", "app_personal_bank")}
                      onRemove={handleRemove("app_personal_bank")}
                    />
                    <SleekUploadTile
                      title="Identification"
                      sub="Passport and/or Driver's Licence (PDF, JPG, or PNG up to 5MB each)"
                      existingFiles={uploads.app_id || uploads.identification || []}
                      uploadingFiles={uploadingFiles.app_id || uploadingFiles.identification || []}
                      onFiles={handleUpload("app_id", "app_id")}
                      onRemove={handleRemove("app_id")}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-lg hover:bg-slate-50"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-semibold text-white bg-sky-600 rounded-lg hover:bg-sky-700 disabled:opacity-70 inline-flex items-center gap-2"
            disabled={isSaving || phonesInvalid}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : "Save Changes"}
          </button>
        </div>
      </div >
    </div >
  );
}
